import { placementBayOf } from "./basePogWalk";
import { sanitizeCdtValue } from "./collapse4";
import {
  SIZE_LIKE_SHARE,
  sizeFamilyOf,
  sizeLikeShare,
} from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { packSizeTierAttrs } from "./sizeTiers";
import { bayShelfCmOf, shelfCmOf, type StoreBay, type StoreFixture } from "./storeFixture";
import {
  effectiveAttrs,
  lengthLabel,
  type LengthUnit,
  isPlaceholderValue,
  type StratumPlan,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";
import { categoryLengthUnitOf } from "./studioLengthUnit";

export type SectionNamingDraft = Pick<StudioDraft, "attrOverrides"> &
  Partial<Pick<StudioDraft, "levelBands" | "measuredBands" | "wallBlocking" | "brandShelves">>;

export const sectionNameOf = (item: StudioItem, sectionAttr: string): string =>
  sanitizeCdtValue(item.attrs[sectionAttr]);

/** Promotions rename CDT0, while section ownership remains anchored to the source item. */
export const namedSectionOf = (
  item: StudioItem,
  sectionAttr: string,
  draft?: SectionNamingDraft,
): string => sanitizeCdtValue((draft ? effectiveAttrs(item, draft) : item.attrs)[sectionAttr]);

export const ROW_HOME_MIN_SHARE = 0.2;

export const ROW_HOME_COVERAGE = 0.6;

export const CAKE_COVERAGE = 0.8;
/** DEV-3694: a row carve side needs this many products. A band of one product becomes a
 *  variant of its own fenced to its rows, which a store with taller shelves turns into a
 *  full-height column for one product (base 75's Brawny: "PT-Br Triple" / "PT-Br Double");
 *  the section ships whole instead. */
export const MIN_CARVE_SKUS = 2;

export const ZONE_OVERLAP_SHARE = 0.2;

export const ZONE_OVERFLOW_TOLERANCE = 0.05;

export const ZONE_SEAM_SHARE = 0.1;

export const DEFAULT_COLUMN_ATTR = "brand";

const IDENTITY_LIKE_SHARE = 0.4;

export const CDT0_NAME_BUDGET = 20;

export interface ZoneShareRule {
  sections: readonly string[];

  share: number;

  interleaveWith?: "left" | "right" | null;
}

export interface PartitionRules {
  zones: readonly ZoneShareRule[];
  warnings: readonly string[];
}

export interface PartitionZone extends ZoneShareRule {
  bays: readonly number[];

  physicalBays?: readonly number[];

  sharedBays?: readonly number[];
}

export interface BayPartitionResult {
  zones: readonly PartitionZone[];
  warnings: readonly string[];
}

export interface MaterializeFixture {
  totalBays: number;
}

export type { StratumPlan };

export interface StrataResult {
  strata: readonly StratumPlan[];

  cdt0Of: ReadonlyMap<string, string>;

  cdt0Order: readonly string[];
  warnings: readonly string[];
}

export interface ReplicaRestrictionRow {
  variant_group_id: string;
  variant: string;
  bay_no: string;
  shelf: string;
  condition: "MUST_NOT_HAVE" | "MUST_HAVE";
}

export const restrictionGroupId = (kind: string, cdt0: string): string =>
  `CDT-STUDIO-REPLICA-${kind}-${cdt0.toUpperCase().replace(/[^A-Z0-9]+/g, "-")}`;

const widthOf = (item: StudioItem): number =>
  item.placements.reduce((total, placement) => total + placement.widthCm, 0);

const salesOf = (item: StudioItem): number => item.salesAmount ?? 0;

interface SectionShape {
  section: string;
  items: StudioItem[];

  lo: number;
  hi: number;
  width: number;
  sales: number;
}

const sectionShapes = (items: readonly StudioItem[], sectionAttr: string): SectionShape[] => {
  const bySection = new Map<string, StudioItem[]>();
  for (const item of items) {
    const section = sectionNameOf(item, sectionAttr);
    if (isPlaceholderValue(section)) continue;
    (bySection.get(section) ?? bySection.set(section, []).get(section)!).push(item);
  }
  const shapes: SectionShape[] = [];
  for (const [section, own] of bySection) {
    // A section spans its placements' edges, not their midpoints: a one-product section
    // measured at its midpoint has no width, so no neighbour could ever overlap it.
    const spans: { lo: number; hi: number; width: number }[] = [];
    for (const item of own) {
      for (const placement of item.placements) {
        spans.push({
          lo: placement.xCm,
          hi: placement.xCm + placement.widthCm,
          width: placement.widthCm,
        });
      }
    }
    spans.sort((a, b) => a.lo + a.hi - (b.lo + b.hi));
    const total = spans.reduce((sum, span) => sum + span.width, 0);
    let acc = 0;
    let lo = spans[0]?.lo ?? 0;
    let hi = spans[spans.length - 1]?.hi ?? 0;
    for (const span of spans) {
      acc += span.width;
      if (acc <= total * 0.05) lo = span.lo;
      if (acc <= total * 0.95) hi = span.hi;
    }
    shapes.push({
      section,
      items: own,
      lo,
      hi: Math.max(hi, lo),
      width: total,
      sales: own.reduce((sum, item) => sum + salesOf(item), 0),
    });
  }
  return shapes.sort((a, b) => a.lo - b.lo);
};

export const deriveZoneShareRules = (
  items: readonly StudioItem[],
  sectionAttr: string,
  runCount = 1,

  wallOrBayWidth?: number | Pick<StudioRunGeometry, "bayWidthCm" | "bays" | "shelves">,

  options: { merge?: boolean; lengthUnit?: LengthUnit } = {},
): PartitionRules => {
  const lengthUnit = options.lengthUnit ?? categoryLengthUnitOf();
  const warnings: string[] = [];
  const shapes = sectionShapes(items, sectionAttr);
  if (shapes.length === 0) return { zones: [], warnings: ["no sections to partition"] };
  if (runCount > 1) {
    warnings.push("partition measured on the first run only; multi-run walls are unpartitioned");
  }
  const wall = typeof wallOrBayWidth === "number" ? undefined : wallOrBayWidth;
  const bayWidthCm = typeof wallOrBayWidth === "number" ? wallOrBayWidth : (wall?.bayWidthCm ?? 0);

  const bayMassOf = (shape: SectionShape): Map<number, number> => {
    const mass = new Map<number, number>();
    if (!(bayWidthCm > 0)) return mass;
    const geometry = { bayWidthCm, bays: Number.MAX_SAFE_INTEGER } as StudioRunGeometry;
    for (const item of shape.items) {
      for (const placement of item.placements) {
        const bay = placementBayOf(placement, geometry);
        mass.set(bay, (mass.get(bay) ?? 0) + placement.widthCm);
      }
    }
    return mass;
  };
  const bayMasses = new Map(shapes.map((shape) => [shape, bayMassOf(shape)]));
  const shareABay = (a: SectionShape, b: SectionShape): boolean => {
    const ma = bayMasses.get(a)!;
    const mb = bayMasses.get(b)!;
    for (const [bay, wa] of ma) {
      const wb = mb.get(bay) ?? 0;
      if (wb > 0 && Math.min(wa, wb) / (wa + wb) >= ZONE_OVERLAP_SHARE) return true;
    }
    return false;
  };

  const zones: SectionShape[][] = [];
  for (const shape of shapes) {
    const current = zones[zones.length - 1];
    const last = current?.[current.length - 1];
    if (last) {
      const overlap = Math.min(last.hi, shape.hi) - Math.max(last.lo, shape.lo);
      const smaller = Math.min(last.hi - last.lo, shape.hi - shape.lo);
      if ((smaller > 0 && overlap / smaller >= ZONE_OVERLAP_SHARE) || shareABay(last, shape)) {
        current.push(shape);
        continue;
      }
    }
    zones.push([shape]);
  }

  const useSales = shapes.some((shape) => shape.sales > 0);
  const weightOf = (zone: SectionShape[]) =>
    zone.reduce((sum, shape) => sum + (useSales ? shape.sales : shape.width), 0);
  const rulesOf = (candidate: SectionShape[][]): ZoneShareRule[] => {
    const totalWeight = candidate.reduce((sum, zone) => sum + weightOf(zone), 0);
    return candidate.map((zone) => ({
      sections: zone.map((shape) => shape.section),
      share: +(totalWeight > 0 ? weightOf(zone) / totalWeight : 1 / candidate.length).toFixed(4),
    }));
  };

  const bayCount = wall?.bays ?? 0;
  const shelfCount = new Set((wall?.shelves ?? []).map((shelf) => shelf.rank)).size;
  const nameOf = (zone: SectionShape[]) => zone.map((shape) => shape.section).join(" + ");
  const massOf = (zone: SectionShape[]) => zone.reduce((sum, shape) => sum + shape.width, 0);
  const sharedBayWidth = (a: SectionShape[], b: SectionShape[]): number => {
    let shared = 0;
    for (const sa of a) {
      for (const [bay, wa] of bayMasses.get(sa)!) {
        for (const sb of b) shared += Math.min(wa, bayMasses.get(sb)!.get(bay) ?? 0);
      }
    }
    return shared;
  };

  const zoneBayMass = (zone: SectionShape[]): Map<number, number> => {
    const mass = new Map<number, number>();
    for (const shape of zone) {
      for (const [bay, width] of bayMasses.get(shape)!) {
        mass.set(bay, (mass.get(bay) ?? 0) + width);
      }
    }
    return mass;
  };

  const seamShare = (a: SectionShape[], b: SectionShape[]): number => {
    const massA = zoneBayMass(a);
    const massB = zoneBayMass(b);
    const acrossThe = (
      mine: Map<number, number>,
      theirs: Map<number, number>,
      myWidth: number,
    ): number => {
      let spilled = 0;
      for (const [bay, width] of mine) if ((theirs.get(bay) ?? 0) > width) spilled += width;
      return myWidth > 0 ? spilled / myWidth : 0;
    };
    return Math.max(acrossThe(massA, massB, massOf(a)), acrossThe(massB, massA, massOf(b)));
  };

  if (options.merge === false) {
    return {
      zones: rulesOf(zones).map((zone, index) => {
        const sideOf = (other: number): number =>
          other >= 0 && other < zones.length ? seamShare(zones[index], zones[other]) : 0;
        const left = sideOf(index - 1);
        const right = sideOf(index + 1);
        const best = Math.max(left, right);
        const interleaveWith = best < ZONE_SEAM_SHARE ? null : right > left ? "right" : "left";
        return { ...zone, interleaveWith };
      }),
      warnings,
    };
  }

  if (bayWidthCm > 0 && bayCount > 0 && shelfCount > 0) {
    for (let guard = 0; zones.length > 1 && guard < shapes.length; guard += 1) {
      const owned = materializePartitionRules(
        { zones: rulesOf(zones), warnings: [] },
        { totalBays: bayCount },
      ).zones;
      const overflowing = zones.findIndex(
        (zone, index) =>
          massOf(zone) >
          owned[index].bays.length * bayWidthCm * shelfCount * (1 + ZONE_OVERFLOW_TOLERANCE),
      );
      if (overflowing < 0) break;
      const zone = zones[overflowing];
      const neighbour = [overflowing - 1, overflowing + 1]
        .filter((index) => index >= 0 && index < zones.length)
        .sort(
          (a, b) =>
            sharedBayWidth(zone, zones[b]) - sharedBayWidth(zone, zones[a]) ||
            weightOf(zones[a]) - weightOf(zones[b]),
        )[0];
      warnings.push(
        `"${nameOf(zone)}" (${lengthLabel(massOf(zone), lengthUnit)}) does not fit the ${owned[overflowing].bays.length} bay(s) its ${(100 * owned[overflowing].share).toFixed(1)}% share earns on this wall (${lengthLabel(owned[overflowing].bays.length * bayWidthCm * shelfCount, lengthUnit)}): it interleaves with "${nameOf(zones[neighbour])}" across their shared bay — one zone.`,
      );
      const lo = Math.min(overflowing, neighbour);
      zones.splice(lo, 2, [...zones[lo], ...zones[lo + 1]]);
    }
  }

  return { zones: rulesOf(zones), warnings };
};

export const materializePartitionRules = (
  rules: PartitionRules,
  fixture: MaterializeFixture,
): BayPartitionResult => {
  const bays = Math.max(fixture.totalBays, 1);
  const out: PartitionZone[] = [];
  let cursor = 0;
  let accShare = 0;
  rules.zones.forEach((zone, index) => {
    accShare += zone.share;
    const boundary =
      index === rules.zones.length - 1
        ? bays
        : Math.min(
            bays - (rules.zones.length - 1 - index),
            Math.max(cursor + 1, Math.round(accShare * bays)),
          );
    const owned: number[] = [];
    for (let bay = cursor + 1; bay <= boundary; bay += 1) owned.push(bay);
    cursor = boundary;
    out.push({ sections: zone.sections, share: zone.share, bays: owned });
  });
  return { zones: out, warnings: rules.warnings };
};

const shelfWidths = (own: readonly StudioItem[]): Map<number, number> => {
  const widths = new Map<number, number>();
  for (const item of own) {
    for (const placement of item.placements) {
      widths.set(placement.shelf, (widths.get(placement.shelf) ?? 0) + placement.widthCm);
    }
  }
  return widths;
};

const rowHomeOf = (own: readonly StudioItem[], shelves: readonly number[]): number[] | null => {
  const widths = shelfWidths(own);
  const total = [...widths.values()].reduce((sum, width) => sum + width, 0);
  if (!(total > 0)) return null;
  const qualifying = shelves.filter(
    (shelf) => (widths.get(shelf) ?? 0) / total >= ROW_HOME_MIN_SHARE,
  );
  if (qualifying.length === 0) return null;
  const runsOfShelves: number[][] = [];
  for (const shelf of qualifying) {
    const current = runsOfShelves[runsOfShelves.length - 1];
    if (current && shelf === current[current.length - 1] + 1) current.push(shelf);
    else runsOfShelves.push([shelf]);
  }
  const best = runsOfShelves
    .map((band) => ({
      band,
      share: band.reduce((sum, shelf) => sum + (widths.get(shelf) ?? 0), 0) / total,
    }))
    .sort((a, b) => b.share - a.share)[0];
  return best.share >= ROW_HOME_COVERAGE ? best.band : null;
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase())
    .join("");

/** A name's initials, keeping more of the first word it differs in from any other name
 *  with the same initials ("BATH TISSUE - Cottonelle" / "- Charmin": "BT-Co" / "BT-Ch";
 *  "Ultra Strong 2-ply" / "Ultra Soft 2-ply": "USt2" / "USo2"), so two sections never
 *  abbreviate to one stratum name. */
export const abbreviationOf = (name: string, names: readonly string[]): string => {
  const words = name.split(/\s+/).filter(Boolean);
  const kept = words.map(() => 1);
  const initials = initialsOf(name);
  for (const other of new Set(names)) {
    if (other === name || initialsOf(other) !== initials) continue;
    const theirs = other.split(/\s+/).filter(Boolean);
    const at = words.findIndex(
      (word, index) => word.toLowerCase() !== theirs[index]?.toLowerCase(),
    );
    if (at < 0) return name;
    const mine = words[at].toLowerCase();
    const their = theirs[at].toLowerCase();
    let length = 1;
    while (length < mine.length && mine.slice(0, length) === their.slice(0, length)) length += 1;
    kept[at] = Math.max(kept[at], length);
  }
  return words.map((word, index) => word[0].toUpperCase() + word.slice(1, kept[index])).join("");
};

interface ShippedZoneOptions {
  draft?: SectionNamingDraft;
  maxZones?: number;
}

/** The merchant's tree owns section order; unsafe or trivial partitions fall back to geometry. */
export const zonesFromShippedOrder = (
  items: readonly StudioItem[],
  sectionAttr: string,
  cdt0Order: readonly string[],
  options: ShippedZoneOptions = {},
): PartitionRules | null => {
  const sections = [...new Set(items.map((item) => sectionNameOf(item, sectionAttr)))].filter(
    (section) => !isPlaceholderValue(section),
  );
  const sectionOfCdt0 = new Map<string, string | null>();
  for (const item of items) {
    const cdt0 = namedSectionOf(item, sectionAttr, options.draft);
    const section = sectionNameOf(item, sectionAttr);
    if (isPlaceholderValue(cdt0) || isPlaceholderValue(section)) continue;
    if (!sectionOfCdt0.has(cdt0)) {
      sectionOfCdt0.set(cdt0, section);
    } else if (sectionOfCdt0.get(cdt0) !== section) {
      sectionOfCdt0.set(cdt0, null);
    }
  }
  const order: string[] = [];
  for (const cdt0 of cdt0Order) {
    const section = sectionOfCdt0.get(cdt0);
    if (section && !order.includes(section)) order.push(section);
  }
  for (const section of sections) if (!order.includes(section)) order.push(section);
  if (order.length < 2) return null;
  const useSales = items.some((item) => (item.salesAmount ?? 0) > 0);
  const weight = new Map<string, number>();
  for (const item of items) {
    const section = sectionNameOf(item, sectionAttr);
    weight.set(
      section,
      (weight.get(section) ?? 0) + (useSales ? (item.salesAmount ?? 0) : widthOf(item)),
    );
  }
  const shapeOf = new Map(sectionShapes(items, sectionAttr).map((shape) => [shape.section, shape]));
  const spanOf = (sections: readonly string[]) => {
    const shapes = sections.flatMap((section) => shapeOf.get(section) ?? []);
    return {
      lo: Math.min(...shapes.map((shape) => shape.lo)),
      hi: Math.max(...shapes.map((shape) => shape.hi)),
    };
  };
  const weighted = order.map((section) => ({
    sections: [section],
    weight: weight.get(section) ?? 0,
    ...spanOf([section]),
  }));
  // Two zones the base stacks in one column (one over the other) belong together.
  const stacked = (a: { lo: number; hi: number }, b: { lo: number; hi: number }) => {
    const smaller = Math.min(a.hi - a.lo, b.hi - b.lo);
    return (
      smaller > 0 && (Math.min(a.hi, b.hi) - Math.max(a.lo, b.lo)) / smaller >= ZONE_OVERLAP_SHARE
    );
  };
  const maxZones = Math.max(options.maxZones ?? Number.MAX_SAFE_INTEGER, 1);
  while (weighted.length > maxZones) {
    // The lightest neighbours merge, the base's own columns first.
    const pairs = weighted.slice(1).map((_, index) => index);
    const columns = pairs.filter((index) => stacked(weighted[index], weighted[index + 1]));
    const pairWeight = (index: number) => weighted[index].weight + weighted[index + 1].weight;
    const lightest = (columns.length > 0 ? columns : pairs).reduce((best, index) =>
      pairWeight(index) < pairWeight(best) ? index : best,
    );
    const left = weighted[lightest];
    const right = weighted[lightest + 1];
    const sections = [...left.sections, ...right.sections];
    weighted.splice(lightest, 2, {
      sections,
      weight: left.weight + right.weight,
      ...spanOf(sections),
    });
  }
  const total = weighted.reduce((sum, zone) => sum + zone.weight, 0);
  const sectionCount = weighted.reduce((sum, zone) => sum + zone.sections.length, 0);
  return {
    zones: weighted.map((zone) => ({
      sections: zone.sections,
      share: +(total > 0 ? zone.weight / total : zone.sections.length / sectionCount).toFixed(4),
      interleaveWith: null,
    })),
    warnings: [],
  };
};

/** Zones on a horizontally blocked wall: the base pog's own bays, numbered across runs.
 *  Each section owns every bay holding at least `ZONE_OVERLAP_SHARE` of its facings; sections
 *  whose bays overlap share one zone, so a brand straddling a seam is never banned from part
 *  of its own space. Each zone runs from its first bay to the next zone's, so materializing
 *  the shares gives the zones back the bays they stand in. A section with no placements joins
 *  the zone of its neighbour in the shipped order. Merging neighbours by sales would cut the
 *  wall mid-bay and move a bay's top-row brand into the bay before it. Null when fewer than
 *  two zones result. */
export const zonesFromBays = (
  items: readonly StudioItem[],
  sectionAttr: string,
  cdt0Order: readonly string[],
  runs: readonly StudioRunGeometry[],
  options: ShippedZoneOptions = {},
): PartitionRules | null => {
  const ordered = [...runs].sort((a, b) => a.run - b.run);
  const offsetOf = new Map<number, number>();
  let totalBays = 0;
  for (const run of ordered) {
    offsetOf.set(run.run, totalBays);
    totalBays += Math.max(run.bays, 1);
  }
  const runOf = new Map(runs.map((run) => [run.run, run]));
  const widthByBay = new Map<string, Map<number, number>>();
  const sections: string[] = [];
  for (const item of items) {
    const section = sectionNameOf(item, sectionAttr);
    if (isPlaceholderValue(section)) continue;
    if (!sections.includes(section)) sections.push(section);
    const byBay = widthByBay.get(section) ?? new Map<number, number>();
    for (const placement of item.placements) {
      const bay =
        (offsetOf.get(placement.run) ?? 0) + placementBayOf(placement, runOf.get(placement.run));
      byBay.set(bay, (byBay.get(bay) ?? 0) + placement.widthCm);
    }
    widthByBay.set(section, byBay);
  }
  /* Each placed section's bay span; overlapping spans merge into one zone. */
  const spans: { sections: string[]; first: number; last: number }[] = [];
  for (const [section, byBay] of widthByBay) {
    const total = [...byBay.values()].reduce((sum, width) => sum + width, 0);
    const widest = Math.max(0, ...byBay.values());
    let own = [...byBay.entries()]
      .filter(([, width]) => total > 0 && width / total >= ZONE_OVERLAP_SHARE)
      .map(([bay]) => bay);
    // Spread so thin no bay holds that share (a full-width row over six bays or more): the
    // section still stands in its bays, so measure them against its widest bay instead.
    if (own.length === 0 && widest > 0) {
      own = [...byBay.entries()]
        .filter(([, width]) => width / widest >= ZONE_OVERLAP_SHARE)
        .map(([bay]) => bay);
    }
    if (own.length === 0) continue;
    spans.push({ sections: [section], first: Math.min(...own), last: Math.max(...own) });
  }
  spans.sort((a, b) => a.first - b.first || a.last - b.last);
  const merged: typeof spans = [];
  for (const span of spans) {
    const previous = merged[merged.length - 1];
    if (previous && span.first <= previous.last) {
      previous.sections.push(...span.sections);
      previous.last = Math.max(previous.last, span.last);
    } else merged.push({ ...span, sections: [...span.sections] });
  }
  if (merged.length < 2) return null;
  const shipped = zonesFromShippedOrder(items, sectionAttr, cdt0Order, { draft: options.draft });
  const shippedOrder = shipped?.zones.flatMap((zone) => zone.sections) ?? [];
  const order = [
    ...shippedOrder.filter((section) => sections.includes(section)),
    ...sections.filter((section) => !shippedOrder.includes(section)),
  ];
  const zoneIndexOf = new Map<string, number>();
  merged.forEach((zone, index) => {
    for (const section of zone.sections) zoneIndexOf.set(section, index);
  });
  // A placeless section follows the section before it in the order, else the one after it.
  order.forEach((section, position) => {
    if (zoneIndexOf.has(section)) return;
    const before = order
      .slice(0, position)
      .reverse()
      .find((other) => zoneIndexOf.has(other));
    const after = order.slice(position + 1).find((other) => zoneIndexOf.has(other));
    const neighbour = before ?? after;
    if (neighbour !== undefined) zoneIndexOf.set(section, zoneIndexOf.get(neighbour) as number);
  });
  return {
    zones: merged.map((zone, index) => {
      const first = index === 0 ? 1 : zone.first;
      const end = index === merged.length - 1 ? totalBays + 1 : merged[index + 1].first;
      return {
        sections: order.filter((section) => zoneIndexOf.get(section) === index),
        share: +((end - first) / totalBays).toFixed(4),
        interleaveWith: null,
      };
    }),
    warnings: [],
  };
};

/** The floor a carved block must clear to earn its own cdt0: below it the
 *  solver culls the variant. Governs stacked slabs and partial cakes alike. */
export interface StackFloor {
  minSkus: number;
  minBayShare: number;
}

/** A block, not a tuck-in. */
export const clearsStackFloor = (
  floor: StackFloor,
  skuCount: number,
  widthCm: number,
  bayWidthCm: number,
): boolean =>
  skuCount >= floor.minSkus || (bayWidthCm > 0 && widthCm >= floor.minBayShare * bayWidthCm);

/** How a section's cake is measured (`deriveStrata`). */
export interface StrataOptions {
  /** Accept a PARTIAL cake: the values whose homes are clean, disjoint bands
   *  become strata even when other values of the attribute have no home or
   *  overlap them; those ride the bare section with no row fence. Default
   *  false: one value without a home refuses the whole cake. */
  partialCakes?: boolean;
  /** The floor a partial-cake band must clear to become a stratum of its own;
   *  a band under it rides the bare section like an orphan. Read only when
   *  `partialCakes` is on; undefined leaves the band share the only gate. */
  bandFloor?: StackFloor;
  /** The category's unit, for the words only; absent, `categoryLengthUnitOf`'s default. */
  lengthUnit?: LengthUnit;
}

/** A partial-cake band must hold at least this share of its section's width. */
export const PARTIAL_CAKE_BAND_SHARE = 0.06;

/** The row holding most of an item's width (a tie goes to the higher row). */
const mainRowOf = (item: StudioItem): number | undefined => {
  const byShelf = new Map<number, number>();
  for (const placement of item.placements) {
    byShelf.set(placement.shelf, (byShelf.get(placement.shelf) ?? 0) + placement.widthCm);
  }
  return [...byShelf.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0];
};

/** A level is a stratum only if it partitions the base's top to bottom sequence: a product
 *  off its value's shelves may sit beside them, never on a shelf another value is fenced to. */
const interleavesFencedBands = (
  bands: readonly { home: readonly number[]; items: readonly StudioItem[] }[],
  shelfCount: number,
): boolean =>
  bands.some((band) =>
    band.items.some((item) => {
      const row = mainRowOf(item);
      if (row === undefined || band.home.includes(row)) return false;
      if (row === band.home[0] - 1 || row === band.home[band.home.length - 1] + 1) return false;
      return bands.some(
        (other) => other !== band && other.home.length < shelfCount && other.home.includes(row),
      );
    }),
  );

export const deriveStrata = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  sectionAttr: string,
  partition: PartitionRules | BayPartitionResult,
  draft?: SectionNamingDraft,
  options: StrataOptions = {},
): StrataResult => {
  const lengthUnit = options.lengthUnit ?? categoryLengthUnitOf();
  const warnings: string[] = [];
  const run = runs[0];
  const shelves = [...new Set((run?.shelves ?? []).map((shelf) => shelf.rank))].sort(
    (a, b) => a - b,
  );
  const zoneOf = new Map<string, number>();
  partition.zones.forEach((zone, index) => {
    for (const section of zone.sections) zoneOf.set(sanitizeCdtValue(section), index);
  });

  if (draft?.measuredBands) {
    if (draft.measuredBands.sectionAttr !== sectionAttr) {
      throw new Error("The measured bands belong to a different root attribute");
    }
    const itemOf = new Map(items.map((item) => [item.skuId, item]));
    const cdt0Of = new Map<string, string>();
    const names = new Set<string>();
    const strata = draft.measuredBands.strata.map((stratum) => {
      if (names.has(stratum.cdt0)) throw new Error("Measured band names must be unique");
      names.add(stratum.cdt0);
      for (const skuId of stratum.skuIds) {
        const item = itemOf.get(skuId);
        if (
          !item ||
          cdt0Of.has(skuId) ||
          namedSectionOf(item, sectionAttr, draft) !== stratum.cdt0 ||
          sectionNameOf(item, sectionAttr) !== stratum.section
        ) {
          throw new Error(`The measured band no longer describes product ${skuId}`);
        }
        cdt0Of.set(skuId, stratum.cdt0);
      }
      return { ...stratum, zone: zoneOf.get(stratum.section) ?? 0 };
    });
    if (cdt0Of.size !== itemOf.size) throw new Error("The measured bands omit products");
    return { strata, cdt0Of, cdt0Order: strata.map((stratum) => stratum.cdt0), warnings };
  }

  // Grouped by the name a promotion gives, so a promoted band becomes its own stratum;
  // the raw section is kept beside it, because zones and sectionOfCdt0 key on that.
  const bySection = new Map<string, StudioItem[]>();
  const rawSectionOf = new Map<string, string>();
  for (const item of items) {
    const section = namedSectionOf(item, sectionAttr, draft);
    if (isPlaceholderValue(section)) continue;
    (bySection.get(section) ?? bySection.set(section, []).get(section)!).push(item);
    if (!rawSectionOf.has(section)) {
      rawSectionOf.set(section, sectionNameOf(item, sectionAttr));
    }
  }

  const strata: StratumPlan[] = [];
  const cdt0Of = new Map<string, string>();
  /* Pack size sits below the sub-line: a size tier ("Small pack" / "Large pack") never cuts a
     brand into zones, any more than the size column it summarises does. */
  const packSizeTiers = packSizeTierAttrs(items);
  const speaksSize = (attr: string, group: readonly StudioItem[]): boolean =>
    packSizeTiers.has(attr) ||
    sizeLikeShare(group.map((item) => item.attrs[attr] ?? null)) >= SIZE_LIKE_SHARE;

  for (const [section, own] of bySection) {
    const rawSection = rawSectionOf.get(section) ?? section;
    const zone = zoneOf.get(rawSection) ?? 0;
    const sectionWidth = own.reduce((sum, item) => sum + widthOf(item), 0);

    const attrs = new Set<string>();
    for (const item of own) {
      for (const [attr, value] of Object.entries(item.attrs)) {
        if (attr === sectionAttr || attr === "subCategory") continue;
        if (!isPlaceholderValue(value)) attrs.add(attr);
      }
    }
    interface Cake {
      attr: string;
      bands: { value: string; home: number[]; items: StudioItem[] }[];
      coverage: number;
    }
    let cake: Cake | null = null;
    let oneSided: Cake | null = null;
    const usedRows = shelves.filter((shelf) =>
      own.some((item) => item.placements.some((placement) => placement.shelf === shelf)),
    );
    for (const attr of attrs) {
      if (speaksSize(attr, own)) continue;
      const byValue = new Map<string, StudioItem[]>();
      for (const item of own) {
        const value = String(item.attrs[attr] ?? "");
        if (isPlaceholderValue(value)) continue;
        (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
      }
      if (byValue.size < 2) continue;
      /* DEV-3531: a one-sided split. The big packs keep to the section's bottom rows while
         the small ones stand everywhere (small packs beside the big ones on the deck): the
         big side is homed on its rows, the small side on every row of the run (full height,
         unanchored, even when the section uses only some rows), so the fence bans the big
         packs from the upper rows and nothing else. */
      if (byValue.size === 2) {
        const sizeAttrs = [...attrs].filter(
          (other) =>
            other !== attr &&
            sizeLikeShare(own.map((item) => item.attrs[other] ?? null)) >= SIZE_LIKE_SHARE,
        );
        const sizesOf = (groupItems: readonly StudioItem[], sizeAttr: string) =>
          groupItems.map((item) => sizeFamilyOf(String(item.attrs[sizeAttr] ?? "")));
        // Magnitudes rank only within one size family (ct vs fl oz has no order).
        const isBigger = (big: readonly StudioItem[], small: readonly StudioItem[]) =>
          sizeAttrs.some((sizeAttr) => {
            const bigSizes = sizesOf(big, sizeAttr);
            const smallSizes = sizesOf(small, sizeAttr);
            const all = [...bigSizes, ...smallSizes];
            if (all.some((size) => !(size.magnitude > 0) || size.family !== all[0].family)) {
              return false;
            }
            const least = Math.min(...bigSizes.map((size) => size.magnitude));
            return least > Math.max(...smallSizes.map((size) => size.magnitude));
          });
        const placedWidth = (groupItems: readonly StudioItem[], rows: readonly number[]) =>
          groupItems.reduce(
            (sum, item) =>
              sum +
              item.placements
                .filter((placement) => rows.includes(placement.shelf))
                .reduce((total, placement) => total + placement.widthCm, 0),
            0,
          );
        const entries = [...byValue.entries()];
        for (const [index, [value, groupItems]] of entries.entries()) {
          const [otherValue, otherItems] = entries[1 - index];
          if (groupItems.length < MIN_CARVE_SKUS || otherItems.length < MIN_CARVE_SKUS) continue;
          if (!isBigger(groupItems, otherItems)) continue;
          const home = rowHomeOf(groupItems, usedRows);
          if (!home || home.length >= usedRows.length) continue;
          if (!home.includes(usedRows[usedRows.length - 1])) continue;
          const width = placedWidth(groupItems, usedRows);
          const inHome = placedWidth(groupItems, home);
          if (!(width > 0) || inHome / width < CAKE_COVERAGE) continue;
          const upper = usedRows.filter((shelf) => !home.includes(shelf));
          const otherWidth = placedWidth(otherItems, usedRows);
          if (!(otherWidth > 0) || placedWidth(otherItems, upper) / otherWidth < ROW_HOME_MIN_SHARE)
            continue;
          const coverage = sectionWidth > 0 ? (inHome + otherWidth) / sectionWidth : 0;
          if (coverage < CAKE_COVERAGE || (oneSided && oneSided.coverage >= coverage)) continue;
          oneSided = {
            attr,
            bands: [
              { value: otherValue, home: shelves, items: otherItems },
              { value, home, items: groupItems },
            ],
            coverage,
          };
        }
      }
      const bands: Cake["bands"] = [];
      let homedWidth = 0;
      let allHomed = true;
      for (const [value, groupItems] of byValue) {
        const home = rowHomeOf(groupItems, shelves);
        if (!home) {
          allHomed = false;
          continue;
        }
        bands.push({ value, home, items: groupItems });

        for (const item of groupItems) {
          for (const placement of item.placements) {
            if (home.includes(placement.shelf)) homedWidth += placement.widthCm;
          }
        }
      }
      if (options.partialCakes) {
        /* Partial cake: keep the homed bands that are mutually disjoint,
           widest first; a band under PARTIAL_CAKE_BAND_SHARE of the section
           is noise. Values without a home, and the losers of an overlap, ride
           the bare section (orphans below). One clean band already says
           something a restriction row can carry. */
        const widthOfBand = (band: Cake["bands"][number]): number =>
          band.items.reduce((sum, item) => sum + widthOf(item), 0);
        const kept: Cake["bands"] = [];
        for (const band of [...bands].sort((a, b) => widthOfBand(b) - widthOfBand(a))) {
          if (sectionWidth > 0 && widthOfBand(band) / sectionWidth < PARTIAL_CAKE_BAND_SHARE) {
            continue;
          }
          if (band.items.length < MIN_CARVE_SKUS) continue;
          if (kept.some((other) => other.home.some((shelf) => band.home.includes(shelf)))) continue;
          kept.push(band);
        }
        if (kept.length === 0) continue;
        // A lone band covering every shelf fences nothing.
        const keptRows = new Set(kept.flatMap((band) => band.home));
        if (kept.length < 2 && keptRows.size >= shelves.length) continue;
        if (interleavesFencedBands(kept, shelves.length)) continue;
        let keptWidth = 0;
        for (const band of kept) {
          for (const item of band.items) {
            for (const placement of item.placements) {
              if (band.home.includes(placement.shelf)) keptWidth += placement.widthCm;
            }
          }
        }
        const coverage = sectionWidth > 0 ? keptWidth / sectionWidth : 0;
        if (
          !cake ||
          coverage > cake.coverage ||
          (coverage === cake.coverage && kept.length > cake.bands.length)
        ) {
          cake = { attr, bands: kept, coverage };
        }
        continue;
      }
      if (!allHomed || bands.length < 2) continue;
      if (bands.some((band) => band.items.length < MIN_CARVE_SKUS)) continue;
      const disjoint = bands.every((a, i) =>
        bands.every((b, j) => i === j || !a.home.some((shelf) => b.home.includes(shelf))),
      );
      if (!disjoint || interleavesFencedBands(bands, shelves.length)) continue;
      const coverage = sectionWidth > 0 ? homedWidth / sectionWidth : 0;
      if (coverage < CAKE_COVERAGE) continue;
      if (
        !cake ||
        coverage > cake.coverage ||
        (coverage === cake.coverage && bands.length > cake.bands.length)
      ) {
        cake = { attr, bands, coverage };
      }
    }

    // A clean cut (disjoint homes) wins; the one-sided split stands in where there is none.
    if (oneSided && (!cake || cake.bands.length < 2)) cake = oneSided;

    if (!cake) {
      const home = rowHomeOf(own, shelves);
      strata.push({
        cdt0: sanitizeCdtValue(section),
        section: rawSection,
        bandAttr: null,
        bandValue: null,
        homeShelves: home ?? shelves,
        zone,
        fullHeight: home === null,
        anchors: home !== null,
        skuIds: own.map((item) => item.skuId),
      });
      for (const item of own) cdt0Of.set(item.skuId, sanitizeCdtValue(section));
      continue;
    }

    const ordered = [...cake.bands].sort((a, b) => a.home[0] - b.home[0]);

    interface ResolvedBand {
      value: string;

      formValue: string;
      home: number[];
      items: StudioItem[];
      refineValue: string | null;
    }

    const liveValuesOf = (attr: string) =>
      own
        .map((item) => String(item.attrs[attr] ?? ""))
        .filter((value) => !isPlaceholderValue(value));
    const preferred = DEFAULT_COLUMN_ATTR;
    const refineAttr =
      attrs.has(preferred) && preferred !== cake.attr
        ? preferred
        : [...attrs]
            .filter((attr) => attr !== cake!.attr && !speaksSize(attr, own))
            .map((attr) => {
              const live = liveValuesOf(attr);
              return {
                attr,
                distinct: new Set(live).size,
                identityLike:
                  live.length > 0 && new Set(live).size / live.length >= IDENTITY_LIKE_SHARE,
              };
            })
            .filter((entry) => !entry.identityLike)
            .sort((a, b) => b.distinct - a.distinct || a.attr.localeCompare(b.attr))[0]?.attr;

    type Band = (typeof ordered)[number];
    interface Carve {
      attr: string;
      value: string;
      home: number[];
      items: StudioItem[];
    }
    const carves: Carve[] = [];
    if (refineAttr) {
      const bandOfSku = new Map<string, Band>();
      for (const band of ordered) for (const item of band.items) bandOfSku.set(item.skuId, band);
      const carveAttrs = [...attrs]
        .filter((attr) => {
          if (attr === cake!.attr || attr === refineAttr) return false;
          if (speaksSize(attr, own)) return false;
          const live = liveValuesOf(attr);
          return live.length > 0 && new Set(live).size / live.length < IDENTITY_LIKE_SHARE;
        })
        .sort();
      const taken = new Set<string>();
      for (const attr of carveAttrs) {
        const byValue = new Map<string, StudioItem[]>();
        for (const item of own) {
          const value = String(item.attrs[attr] ?? "");
          if (isPlaceholderValue(value)) continue;
          (byValue.get(value) ?? byValue.set(value, []).get(value)!).push(item);
        }
        for (const value of [...byValue.keys()].sort()) {
          const groupItems = byValue.get(value)!.filter((item) => !taken.has(item.skuId));
          if (groupItems.length < 2) continue;
          const brands = new Set(
            groupItems
              .map((item) => String(item.attrs[refineAttr] ?? ""))
              .filter((v) => !isPlaceholderValue(v)),
          );
          if (brands.size < 2) continue;
          const bands = new Set(groupItems.map((item) => bandOfSku.get(item.skuId)));
          if (bands.size !== 1 || bands.has(undefined)) continue;
          const band = [...bands][0]!;
          const home = rowHomeOf(groupItems, shelves);
          if (!home || home.every((shelf) => band.home.includes(shelf))) continue;
          let width = 0;
          let inHome = 0;
          for (const item of groupItems) {
            for (const placement of item.placements) {
              width += placement.widthCm;
              if (home.includes(placement.shelf)) inHome += placement.widthCm;
            }
          }
          if (!(width > 0) || inHome / width < CAKE_COVERAGE) continue;

          const groupIds = new Set(groupItems.map((item) => item.skuId));
          let isBlock = true;
          let previous: readonly [number, number] | null = null;
          for (const shelf of home) {
            const mine = groupItems.flatMap((item) =>
              item.placements.filter((p) => p.shelf === shelf),
            );
            if (mine.length === 0) {
              isBlock = false;
              break;
            }
            const lo = Math.min(...mine.map((p) => p.xCm));
            const hi = Math.max(...mine.map((p) => p.xCm + p.widthCm));
            const intruded = own.some(
              (item) =>
                !groupIds.has(item.skuId) &&
                item.placements.some((p) => {
                  const mid = p.xCm + p.widthCm / 2;
                  return p.shelf === shelf && mid > lo && mid < hi;
                }),
            );
            if (intruded || (previous && (hi <= previous[0] || lo >= previous[1]))) {
              isBlock = false;
              break;
            }
            previous = [lo, hi];
          }
          if (!isBlock) continue;
          for (const item of groupItems) taken.add(item.skuId);
          band.items = band.items.filter((item) => !groupIds.has(item.skuId));
          carves.push({ attr, value, home, items: groupItems });
          warnings.push(
            `"${section}"'s ${attr} = "${value}" (${groupItems.length} SKUs, ${brands.size} ${refineAttr}s) stands as one block on rows ${home.join(",")} inside the "${band.value}" band (rows ${band.home.join(",")}): the band's fence would cut through it, so it leaves the band and becomes its own stratum with that row-home.`,
          );
        }
      }
    }

    for (const band of ordered) {
      const staying: StudioItem[] = [];
      for (const item of band.items) {
        const row = mainRowOf(item);
        if (row === undefined || band.home.includes(row)) {
          staying.push(item);
          continue;
        }
        const host = ordered.find((other) => other !== band && other.home.includes(row));
        if (!host) {
          staying.push(item);
          warnings.push(
            `"${item.name}" (${section} ${band.value}) stands on row ${row}, which no band of "${section}" calls home; it stays with the "${band.value}" band and the fence brings it onto rows ${band.home.join(",")}.`,
          );
          continue;
        }
        host.items.push(item);
        warnings.push(
          `"${item.name}" is a "${band.value}" by attribute but stands on row ${row}, the "${host.value}" band's rows: it joins that stratum — contiguous with its brand there — never a fenceless straggler.`,
        );
      }
      band.items = staying;
    }

    const brandOf = (item: StudioItem): string =>
      refineAttr ? String(item.attrs[refineAttr] ?? "") : "";
    const sectionBrands = new Set(
      own.map(brandOf).filter((value) => value !== "" && !isPlaceholderValue(value)),
    );
    const refined: ResolvedBand[] = ordered.map((band) => {
      const brands = new Set(
        band.items.map(brandOf).filter((value) => value !== "" && !isPlaceholderValue(value)),
      );
      const inBand = new Set(band.items.map((item) => item.skuId));
      const [brand] = [...brands];
      const elsewhere =
        brands.size === 1 && own.some((item) => !inBand.has(item.skuId) && brandOf(item) === brand);
      if (brands.size === 1 && sectionBrands.size > 1 && elsewhere && band.items.length > 0) {
        warnings.push(
          `the "${section} ${band.value}" band's rows are "${brand}"'s alone (${band.items.length} SKUs): the promotion is named for that intersection.`,
        );
        return {
          value: `${brand} ${band.value}`,
          formValue: band.value,
          home: band.home,
          items: band.items,
          refineValue: brand,
        };
      }
      return {
        value: band.value,
        formValue: band.value,
        home: band.home,
        items: band.items,
        refineValue: null,
      };
    });

    /* A band too small to earn a mudmap cell of its own is a tier, not a
       stratum: it takes no cdt0 and its items ride the bare section with the
       orphans below. The same floor the stacked-brand slabs clear
       (`StackFloor`), and only under `partialCakes` — a full cake has no
       residual stratum for them to ride. */
    const bandFloor = options.partialCakes ? options.bandFloor : undefined;
    const standing = bandFloor
      ? refined.filter((band) => {
          const width = band.items.reduce((sum, item) => sum + widthOf(item), 0);
          if (
            band.items.length === 0 ||
            clearsStackFloor(bandFloor, band.items.length, width, run?.bayWidthCm ?? 0)
          ) {
            return true;
          }
          warnings.push(
            `the "${section} ${band.value}" band holds ${band.items.length} SKU(s) over ${lengthLabel(width, lengthUnit)} on rows ${band.home.join(",")} — a tier, not a stratum of its own (floor: ${bandFloor.minSkus} SKUs or ${bandFloor.minBayShare === 1 ? "a whole bay" : `${bandFloor.minBayShare} of a bay`}); a variant that small earns no cell, so its items ride "${section}" with the orphans.`,
          );
          return false;
        })
      : refined;

    const longest = Math.max(
      ...standing.map((band) => sanitizeCdtValue(`${section} ${band.value}`).length),
      ...carves.map((carve) => sanitizeCdtValue(`${section} ${carve.value}`).length),
    );
    const abbreviated = longest > CDT0_NAME_BUDGET;
    const prefix = abbreviated ? abbreviationOf(section, [...bySection.keys()]) : section;
    if (abbreviated) {
      warnings.push(
        `"${section}" strata abbreviate the parent to "${prefix}" — the full compound names blow the ${CDT0_NAME_BUDGET}-character budget, and a family renames as one vocabulary or not at all.`,
      );
    }
    standing.forEach((band, index) => {
      const last = index === standing.length - 1;
      if (band.items.length === 0) {
        warnings.push(
          `the "${section} ${band.formValue}" band is empty once its items find their rows; no stratum.`,
        );
        return;
      }
      const narrowedCdt0 = sanitizeCdtValue(`${prefix} ${band.value}`);
      // A partial cake leaves orphans riding the bare section name, so no band
      // may take that name too: two strata under one cdt0 would fence each
      // other's items.
      const residual = !options.partialCakes && last && !abbreviated && band.refineValue === null;
      const cdt0 = sanitizeCdtValue(residual ? section : narrowedCdt0);
      strata.push({
        cdt0,
        section: rawSection,
        bandAttr: cake!.attr,
        bandValue: band.value,
        homeShelves: band.home,
        zone,
        // The free side of a one-sided split shares every row: no fence, no anchor.
        fullHeight: band.home.length >= shelves.length,
        anchors: band.home.length < shelves.length,
        skuIds: band.items.map((item) => item.skuId),
      });
      for (const item of band.items) cdt0Of.set(item.skuId, cdt0);
    });

    for (const carve of carves) {
      const cdt0 = sanitizeCdtValue(`${prefix} ${carve.value}`);
      strata.push({
        cdt0,
        section: rawSection,
        bandAttr: carve.attr,
        bandValue: carve.value,
        homeShelves: carve.home,
        zone,
        fullHeight: false,
        anchors: true,
        skuIds: carve.items.map((item) => item.skuId),
      });
      for (const item of carve.items) cdt0Of.set(item.skuId, cdt0);
    }

    const claimed = new Set([
      ...standing.flatMap((band) => band.items.map((item) => item.skuId)),
      ...carves.flatMap((carve) => carve.items.map((item) => item.skuId)),
    ]);
    const orphans = own.filter((item) => !claimed.has(item.skuId));
    if (orphans.length > 0) {
      const cdt0 = sanitizeCdtValue(section);
      strata.push({
        cdt0,
        section: rawSection,
        bandAttr: null,
        bandValue: null,
        homeShelves: shelves,
        zone,
        fullHeight: true,
        anchors: false,
        skuIds: orphans.map((item) => item.skuId),
      });
      for (const item of orphans) cdt0Of.set(item.skuId, cdt0);
      warnings.push(
        `${orphans.length} "${section}" SKUs sit outside every promoted band entirely; they ride "${cdt0}" with no row-home of their own.`,
      );
    }
  }

  const rankOf = (stratum: StratumPlan): readonly number[] => [
    stratum.zone,
    stratum.fullHeight && stratum.bandAttr === null ? 1 : 0,
    stratum.homeShelves[0] ?? 0,
  ];
  const orderedStrata = [...strata].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    for (let index = 0; index < ra.length; index += 1) {
      if (ra[index] !== rb[index]) return ra[index] - rb[index];
    }
    return a.cdt0.localeCompare(b.cdt0);
  });

  return {
    strata: orderedStrata,
    cdt0Of,
    cdt0Order: orderedStrata.map((stratum) => stratum.cdt0),
    warnings,
  };
};

export const materializeRestrictionRows = (
  strata: readonly StratumPlan[],
  partition: BayPartitionResult,
  runs: readonly StudioRunGeometry[],

  items?: readonly StudioItem[],
  options: FenceOptions = {},
): { rows: ReplicaRestrictionRow[]; anchors: ReplicaRestrictionRow[] } => {
  const run = runs[0];
  const shelves = [...new Set((run?.shelves ?? []).map((shelf) => shelf.rank))].sort(
    (a, b) => a - b,
  );
  const allBays = Array.from({ length: Math.max(run?.bays ?? 0, 0) }, (_, index) => index + 1);

  const itemOf = new Map((items ?? []).map((item) => [item.skuId, item]));
  const bayOfPlacement = (placement: StudioItem["placements"][number]): number =>
    placementBayOf(placement, runs.find((entry) => entry.run === placement.run) ?? run);
  const standsInBay = (stratum: StratumPlan, bay: number): boolean =>
    !items ||
    stratum.skuIds.some((skuId) =>
      (itemOf.get(skuId)?.placements ?? []).some((placement) => bayOfPlacement(placement) === bay),
    );
  /** bay -> shelves the stratum's own items stand on there (base wall). */
  const occupiedOf = (stratum: StratumPlan): Map<number, Set<number>> => {
    const occupied = new Map<number, Set<number>>();
    if (!items) return occupied;
    for (const skuId of stratum.skuIds) {
      for (const placement of itemOf.get(skuId)?.placements ?? []) {
        const bay = bayOfPlacement(placement);
        (occupied.get(bay) ?? occupied.set(bay, new Set()).get(bay)!).add(placement.shelf);
      }
    }
    return occupied;
  };
  /** Bays where the stratum's SECTION stands on the base: a foreign-zone bay
   *  among them is a seam the base itself interleaves across. */
  const sectionBaysOf = (section: string): Set<number> => {
    const bays = new Set<number>();
    if (!items) return bays;
    for (const stratum of strata) {
      if (stratum.section !== section) continue;
      for (const bay of occupiedOf(stratum).keys()) bays.add(bay);
    }
    return bays;
  };

  /** bay -> shelves ANY stratum stands on there. A shelf nobody stands on is
   *  one the base left empty; pogger fills every shelf, so someone must be
   *  allowed there. */
  const anyoneIn = new Map<number, Set<number>>();
  if (items) {
    for (const stratum of strata) {
      for (const [bay, ranks] of occupiedOf(stratum)) {
        const there = anyoneIn.get(bay) ?? anyoneIn.set(bay, new Set()).get(bay)!;
        for (const rank of ranks) there.add(rank);
      }
    }
  }

  const rows: ReplicaRestrictionRow[] = [];
  const anchors: ReplicaRestrictionRow[] = [];
  const partitioned = partition.zones.length > 1;

  for (const stratum of strata) {
    if (stratum.skuIds.length === 0) continue;
    const zone = partition.zones[stratum.zone];
    const ownBays = partitioned ? (zone?.bays ?? allBays) : allBays;
    const shared =
      options.sharedSeams && items ? sectionBaysOf(stratum.section) : new Set<number>();
    /* A SEAM the fixture side lent this zone (`PartitionZone.sharedBays`) is
       open to it whatever the base wall did there: the neighbour owns the bay
       and neither zone bans it. `materializePartitionRules` lends none, so this
       is empty unless the zones came from `allocateZoneBays`. */
    for (const bay of zone?.sharedBays ?? []) shared.add(bay);
    const foreignBays = partitioned
      ? allBays.filter((bay) => !ownBays.includes(bay) && !shared.has(bay))
      : [];
    const home = new Set(stratum.homeShelves);
    const occupied = options.perBay && items ? occupiedOf(stratum) : new Map<number, Set<number>>();
    /** Shelves the stratum may use in one of its own (or shared seam) bays. */
    const openIn = (bay: number): ReadonlySet<number> => {
      const there = occupied.get(bay);
      if (there && there.size > 0) {
        /* Per-bay fence: the shelves it stands on, plus the shelves of its home
           the base left EMPTY in this bay. A cell no stratum may use is a dead
           cell, and pogger's shelf minimum makes the whole request infeasible
           (aisle 1 PSA, bay 13 shelves 2-3: powder's home rows, nothing on
           them, 2026-09-22). The band whose home covers the empty shelf is the
           one that may fill it. */
        const open = new Set(there);
        const taken = anyoneIn.get(bay) ?? new Set<number>();
        for (const shelf of stratum.fullHeight ? shelves : home) {
          if (!taken.has(shelf)) open.add(shelf);
        }
        return open;
      }
      return stratum.fullHeight ? new Set(shelves) : home;
    };
    const fencedBays = [...ownBays, ...[...shared].filter((bay) => !ownBays.includes(bay))];
    for (const shelf of shelves) {
      const banned = new Set<number>(foreignBays);
      for (const bay of fencedBays) if (!openIn(bay).has(shelf)) banned.add(bay);
      if (banned.size === 0) continue;
      const isFence = [...banned].some((bay) => !foreignBays.includes(bay));
      rows.push({
        variant_group_id: restrictionGroupId(isFence ? "CAKE" : "PARTITION", stratum.cdt0),
        variant: stratum.cdt0,
        bay_no: [...banned].sort((a, b) => a - b).join(" | "),
        shelf: String(shelf),
        condition: "MUST_NOT_HAVE",
      });
    }
    // The first bay of each zone anchors its own strata (see
    // `StratumPlan.anchors` and `items`).
    const ownFirstBay =
      stratum.anchorOwnBay && items
        ? [...occupiedOf(stratum).keys()].sort((a, b) => a - b)[0]
        : undefined;
    const anchorBay = ownFirstBay ?? ownBays[0];
    /* A stacked slab takes no MUST_HAVE at all: its per-bay fence already
       confines it to the cells it stood on, and a block pinned inside a seam
       bay (one two families share) holds the family rectangle across the seam
       at a fixed minimum width so the seam cannot tile - run_ab117_opts2 was
       INFEASIBLE on exactly that (2026-09-22; feasible with candidate_width,
       seam tiling or the family rectangle off, or with the five slab anchors
       dropped). The materializer never emitted them either. */
    if (
      stratum.anchors &&
      !stratum.fullHeight &&
      !stratum.anchorOwnBay &&
      ownBays.length > 0 &&
      anchorBay !== undefined &&
      standsInBay(stratum, anchorBay)
    ) {
      // Per-bay fences anchor only the home shelves the stratum stands on in
      // that bay; a MUST_HAVE on an empty base cell is unsatisfiable.
      const anchorShelves =
        options.perBay || stratum.anchorOwnBay
          ? stratum.homeShelves.filter((shelf) =>
              (
                occupied.get(anchorBay) ??
                occupiedOf(stratum).get(anchorBay) ??
                openIn(anchorBay)
              ).has(shelf),
            )
          : stratum.homeShelves;
      for (const shelf of anchorShelves) {
        anchors.push({
          variant_group_id: restrictionGroupId("ANCHOR", stratum.cdt0),
          variant: stratum.cdt0,
          bay_no: String(anchorBay),
          shelf: String(shelf),
          condition: "MUST_HAVE",
        });
      }
    }
  }
  return { rows, anchors };
};

export interface RowHomeRule {
  cdt0: string;
  cdt1: string;
  attr: string;
  value: string;

  shelves: readonly number[];
  skuIds: readonly string[];
  /** Overrides the sub-stratum's name suffix (`"<parent> <label>"`). A stacked
   *  brand carved on the cdt1 attribute itself names the brand once, not
   *  `"<brand> <brand>"`. */
  label?: string;
}

/** How the base-wall fences are drawn (`materializeRestrictionRows`). */
export interface FenceOptions {
  /** Fence each stratum PER BAY to the shelves it actually stands on in that
   *  bay (a ragged band edge stays ragged); bays it does not reach keep the
   *  zone-wide home fence. Default false: one home fence over every own bay. */
  perBay?: boolean;
  /** A foreign-zone bay the stratum's own section stands in on the base is a
   *  SHARED seam bay and carries no partition ban for it. Default false: every
   *  foreign bay is banned, and the base's own spill is re-homed. */
  sharedSeams?: boolean;
}

export interface RowHomeStrataResult extends StrataResult {
  rowHomeStrata: readonly StratumPlan[];

  rowHomeWarnings: readonly string[];
}

export const withRowHomeStrata = (
  base: StrataResult,
  rowHomes: readonly RowHomeRule[],
): RowHomeStrataResult => {
  const rowHomeWarnings: string[] = [];
  const strata: StratumPlan[] = base.strata.map((stratum) => ({ ...stratum }));
  const cdt0Of = new Map(base.cdt0Of);
  const cdt0Order = [...base.cdt0Order];
  const known = new Set(strata.map((stratum) => stratum.cdt0));
  const rowHomeStrata: StratumPlan[] = [];

  const carvedFrom = new Map<string, string>();

  interface RowHomeGroup {
    rule: RowHomeRule;
    cdt1s: string[];
    skuIds: string[];
  }
  const groups = new Map<string, RowHomeGroup>();
  for (const rule of rowHomes) {
    const shelves = [...new Set(rule.shelves)].sort((a, b) => a - b);
    const key = [rule.cdt0, rule.attr, rule.value, shelves.join(",")].join("\u0000");
    const group =
      groups.get(key) ??
      groups.set(key, { rule: { ...rule, shelves }, cdt1s: [], skuIds: [] }).get(key)!;
    if (!group.cdt1s.includes(rule.cdt1)) group.cdt1s.push(rule.cdt1);
    for (const skuId of rule.skuIds) if (!group.skuIds.includes(skuId)) group.skuIds.push(skuId);
  }
  const orderIndex = (cdt0: string) => {
    const index = cdt0Order.indexOf(cdt0);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  const ordered = [...groups.values()].sort(
    (a, b) =>
      orderIndex(a.rule.cdt0) - orderIndex(b.rule.cdt0) ||
      (a.rule.shelves[0] ?? 0) - (b.rule.shelves[0] ?? 0) ||
      a.cdt1s.join("|").localeCompare(b.cdt1s.join("|")) ||
      a.rule.value.localeCompare(b.rule.value),
  );

  for (const group of ordered) {
    const { rule } = group;
    const parentIndex = strata.findIndex((stratum) => stratum.cdt0 === rule.cdt0);
    const parent = parentIndex >= 0 ? strata[parentIndex] : undefined;
    const scope = group.cdt1s.length >= 2 ? rule.value : `${group.cdt1s[0]} ${rule.value}`;
    const label = `"${rule.cdt0} › ${group.cdt1s.join(" / ")}" ${rule.attr} = ${rule.value}`;
    if (!parent) {
      rowHomeWarnings.push(
        `${label}: no stratum ships "${rule.cdt0}"; the row-home has no parent to carve and is dropped.`,
      );
      continue;
    }
    if (
      rule.shelves.length === 0 ||
      parent.homeShelves.every((shelf) => rule.shelves.includes(shelf))
    ) {
      rowHomeWarnings.push(
        `${label} sits on rows ${rule.shelves.join(",")} — the parent's whole home; a row-home that fences nothing is not a rule.`,
      );
      continue;
    }

    const free = group.skuIds.filter((skuId) => cdt0Of.get(skuId) === parent.cdt0);
    if (free.length < group.skuIds.length) {
      rowHomeWarnings.push(
        `${label}: ${group.skuIds.length - free.length} SKU(s) already carved by an earlier row-home stand there; only the rest move.`,
      );
    }
    if (free.length < 2) {
      rowHomeWarnings.push(
        `${label} holds ${free.length} SKU(s): a straggler rides ordering, never a restriction (rule 19d).`,
      );
      continue;
    }
    const suffix = rule.label ?? scope;
    let cdt0 = sanitizeCdtValue(`${parent.cdt0} ${suffix}`);
    if (cdt0.length > CDT0_NAME_BUDGET) {
      const abbreviated = sanitizeCdtValue(
        `${abbreviationOf(
          parent.cdt0,
          strata.map((stratum) => stratum.cdt0),
        )} ${suffix}`,
      );
      rowHomeWarnings.push(
        `"${cdt0}" abbreviates its parent to "${abbreviated}" — over the ${CDT0_NAME_BUDGET}-character budget.`,
      );
      cdt0 = abbreviated;
    }
    if (known.has(cdt0)) continue;
    known.add(cdt0);
    const stratum: StratumPlan = {
      cdt0,
      section: parent.section,
      bandAttr: rule.attr,
      bandValue: rule.value,
      homeShelves: rule.shelves,
      zone: parent.zone,
      fullHeight: false,
      // A brand-scoped sub-stratum never anchors on the zone's edge; see
      // `StratumPlan.anchors`. A stacked brand (labelled rule) anchors on its
      // OWN first bay instead: the wall put it there.
      anchors: group.cdt1s.length >= 2 || rule.label !== undefined,
      skuIds: free,
      ...(rule.label !== undefined ? { anchorOwnBay: true, parentCdt0: parent.cdt0 } : {}),
    };
    for (const skuId of free) cdt0Of.set(skuId, cdt0);
    const moved = new Set(free);
    strata[parentIndex] = { ...parent, skuIds: parent.skuIds.filter((skuId) => !moved.has(skuId)) };

    let at = parentIndex + 1;
    while (at < strata.length && rowHomeStrata.includes(strata[at])) at += 1;
    strata.splice(at, 0, stratum);
    const orderAt = cdt0Order.indexOf(parent.cdt0);
    let orderInsert = orderAt < 0 ? cdt0Order.length : orderAt + 1;
    while (
      orderInsert < cdt0Order.length &&
      rowHomeStrata.some((entry) => entry.cdt0 === cdt0Order[orderInsert])
    ) {
      orderInsert += 1;
    }
    cdt0Order.splice(orderInsert, 0, cdt0);
    rowHomeStrata.push(stratum);
    carvedFrom.set(cdt0, parent.cdt0);
    rowHomeWarnings.push(
      `${label} (${free.length} SKUs) owns rows ${rule.shelves.join(",")} of the node — a vertical split cdt2 cannot carry; concatenated upward into the ${group.cdt1s.length >= 2 ? "shared" : "brand-scoped"} cdt0 sub-stratum "${cdt0}" with that row-home.`,
    );
  }

  const carvedParents = new Set(carvedFrom.values());
  for (let index = strata.length - 1; index >= 0; index -= 1) {
    const stratum = strata[index];
    if (stratum.skuIds.length > 0 || !carvedParents.has(stratum.cdt0)) continue;
    strata.splice(index, 1);
    const orderAt = cdt0Order.indexOf(stratum.cdt0);
    if (orderAt >= 0) cdt0Order.splice(orderAt, 1);
    rowHomeWarnings.push(
      `every SKU of "${stratum.cdt0}" moved into its row-home sub-strata (${[...carvedFrom]
        .filter(([, parentCdt0]) => parentCdt0 === stratum.cdt0)
        .map(([subCdt0]) => `"${subCdt0}"`)
        .join(
          ", ",
        )}); it is no longer a stratum and emits no rows — a row naming it would match no candidate.`,
    );
  }

  return {
    strata,
    cdt0Of,
    cdt0Order,
    warnings: [...base.warnings, ...rowHomeWarnings],
    rowHomeStrata,
    rowHomeWarnings,
  };
};

export const ROW_SHARE_TIE = 0.05;

export type BandKind = "band" | "column" | "fullHeight";

export interface BandSpan {
  fromCdt0: string;
  fromEdge: "top" | "bottom";
  toCdt0: string;
  toEdge: "top" | "bottom";
}

export interface SharedCarveRule {
  section: string;

  parentCdt0: string;
  attr: string;
  smallCdt0: string;
  largeCdt0: string;
}

export interface BandRule {
  cdt0: string;
  section: string;
  bandAttr: string | null;
  bandValue: string | null;

  order: number;
  kind: BandKind;

  baseRows: number;
  /** Those rows' ranks on the base (1 = top), for the cake's shape. */
  baseHome?: readonly number[];
  baseShelves: number;
  baseRowShare: number;

  baseWidthCm: number;
  minWidthCm: number;

  anchorsAtZoneEdge: boolean;
  span?: BandSpan;

  carve?: SharedCarveRule;
  /** A stacked-brand slab: it stands inside `parentCdt0`'s rows, `topOffset`
   *  rows below the parent's top and `bottomOffset` rows above its bottom, of
   *  the `parentRows` the parent held on the base. The store scales both. */
  nest?: NestRule;
  /** Measured on the base: the band anchors on the first bay IT stands in, not
   *  the zone's edge. Another store has no such bay, so no anchor is emitted. */
  anchorsOwnBay?: boolean;
  /** A parent every brand left: all its items were carved into stacked slabs,
   *  so it ships nothing itself and no candidate carries its name. It stands
   *  in the cake for its slabs, which nest in its rows; it emits no row. */
  virtual?: boolean;
}

export interface NestRule {
  parentCdt0: string;
  topOffset: number;
  bottomOffset: number;
  parentRows: number;
}

export interface BaseCarve {
  section?: string;
  parentCdt0: string;
  attr: string;
  smallCdt0: string;
  largeCdt0: string;
}

export interface BaseCarveSide {
  cdt0: string;
  parentCdt0: string;
  attr: string;
  side: string;
  sideOrder: number;
}

const carvePairsOf = (
  carves: readonly (BaseCarve | BaseCarveSide)[],
  warn: (message: string) => void,
): BaseCarve[] => {
  const pairs: BaseCarve[] = [];
  const sides = new Map<string, BaseCarveSide[]>();
  for (const carve of carves) {
    if ("smallCdt0" in carve) {
      pairs.push(carve);
      continue;
    }
    const own =
      sides.get(carve.parentCdt0) ?? sides.set(carve.parentCdt0, []).get(carve.parentCdt0)!;
    own.push(carve);
  }
  for (const [parentCdt0, own] of sides) {
    if (own.length !== 2) {
      warn(
        `"${parentCdt0}" has ${own.length} size side(s) on the base; a shared row split needs two, so the sides keep their own rows.`,
      );
      continue;
    }
    const [small, large] = [...own].sort((a, b) => a.sideOrder - b.sideOrder);
    pairs.push({
      parentCdt0,
      attr: small.attr,
      smallCdt0: small.cdt0,
      largeCdt0: large.cdt0,
    });
  }
  return pairs;
};

export interface DeriveBandRulesOptions {
  baseShelves?: readonly number[];

  baseWidthCm?: ReadonlyMap<string, number>;
  minWidthCm?: ReadonlyMap<string, number>;

  carves?: readonly (BaseCarve | BaseCarveSide)[];
}

const uniqueSorted = (values: readonly number[]): number[] =>
  [...new Set(values)].sort((a, b) => a - b);

const contiguousRanks = (ranks: readonly number[]): boolean =>
  ranks.every((rank, index) => index === 0 || rank === ranks[index - 1] + 1);

export type BandRules = readonly BandRule[] & { readonly warnings: readonly string[] };

export const deriveBandRules = (
  base: Pick<StrataResult, "strata" | "cdt0Order"> | readonly StratumPlan[],
  maybeOptions: DeriveBandRulesOptions | PartitionRules = {},
): BandRules => {
  const warnings: string[] = [];
  const asArray = Array.isArray(base);
  const strataIn: readonly StratumPlan[] = asArray
    ? (base as readonly StratumPlan[])
    : (base as Pick<StrataResult, "strata">).strata;
  const cdt0OrderIn: readonly string[] = asArray
    ? (base as readonly StratumPlan[]).map((stratum) => stratum.cdt0)
    : ((base as Pick<StrataResult, "cdt0Order">).cdt0Order ??
      strataIn.map((stratum) => stratum.cdt0));
  const options: DeriveBandRulesOptions =
    maybeOptions && typeof maybeOptions === "object" && !("zones" in maybeOptions)
      ? (maybeOptions as DeriveBandRulesOptions)
      : {};
  const baseShelves = uniqueSorted([
    ...(options.baseShelves ?? strataIn.flatMap((stratum) => [...stratum.homeShelves])),
  ]);
  const shelfCount = Math.max(baseShelves.length, 1);
  const orderOf = (cdt0: string) => {
    const index = cdt0OrderIn.indexOf(cdt0);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };

  interface Draft {
    cdt0: string;
    section: string;
    bandAttr: string | null;
    bandValue: string | null;
    home: number[];
    kind: BandKind;
    anchors: boolean;
    baseWidthCm: number;
    minWidthCm: number;
    carve?: SharedCarveRule;
    nest?: NestRule;
    anchorsOwnBay?: boolean;
    virtual?: boolean;
  }
  const widthOfCdt0 = (cdt0: string) => options.baseWidthCm?.get(cdt0) ?? 0;
  const minOfCdt0 = (cdt0: string) => options.minWidthCm?.get(cdt0) ?? 0;
  const carvePairs = carvePairsOf(options.carves ?? [], (message) => warnings.push(message));

  const bySection = new Map<string, StratumPlan[]>();
  for (const stratum of strataIn) {
    const own =
      bySection.get(stratum.section) ?? bySection.set(stratum.section, []).get(stratum.section)!;
    own.push(stratum);
  }

  const drafts: Draft[] = [];
  for (const [section, own] of bySection) {
    // A parent every brand left (all its items carved into slabs) ships no
    // item and is no stratum, but its slabs still stand in ITS rows: it comes
    // back as a virtual band, the union of its slabs' rows, no items, no
    // anchor of its own.
    const virtualParents = new Map<string, StratumPlan>();
    for (const stratum of own) {
      const parent = stratum.parentCdt0;
      if (parent === undefined) continue;
      if (own.some((other) => other.cdt0 === parent) || virtualParents.has(parent)) continue;
      const slabs = own.filter((other) => other.parentCdt0 === parent);
      virtualParents.set(parent, {
        cdt0: parent,
        section,
        bandAttr: null,
        bandValue: null,
        homeShelves: uniqueSorted(slabs.flatMap((slab) => [...slab.homeShelves])),
        zone: stratum.zone,
        fullHeight: false,
        anchors: false,
        skuIds: [],
      });
    }
    if (virtualParents.size > 0) {
      own.push(...virtualParents.values());
      for (const parent of virtualParents.keys()) {
        warnings.push(
          `"${parent}" ships no item of its own (every brand in it was carved into a slab); it stands in the cake as the band its slabs share.`,
        );
      }
    }
    const slabsOfParent = (parent: string) => own.filter((other) => other.parentCdt0 === parent);
    const homes = own.map((stratum) => uniqueSorted([...stratum.homeShelves]));
    const isFull = own.map(
      (stratum, index) =>
        stratum.fullHeight || baseShelves.every((rank) => homes[index].includes(rank)),
    );
    // The cake is the largest set of strata with disjoint rows; drop the worst
    // offender until they are. A stratum running through the others spans instead.
    const parentIndexOf = own.map((stratum) =>
      stratum.parentCdt0 === undefined
        ? -1
        : own.findIndex((other) => other.cdt0 === stratum.parentCdt0),
    );
    const isNested = parentIndexOf.map((parent) => parent >= 0);
    const overlapping = own.map(() => false);
    const live = own.map((_, index) => index).filter((index) => !isFull[index] && !isNested[index]);
    for (let guard = 0; guard < own.length; guard += 1) {
      const clashes = live.map((index) => ({
        index,
        count: live.filter(
          (other) => other !== index && homes[other].some((rank) => homes[index].includes(rank)),
        ).length,
      }));
      const worst = clashes
        .filter((entry) => entry.count > 0)
        .sort(
          (a, b) =>
            b.count - a.count ||
            homes[a.index].length - homes[b.index].length ||
            own[a.index].cdt0.localeCompare(own[b.index].cdt0),
        )[0];
      if (!worst) break;
      overlapping[worst.index] = true;
      live.splice(live.indexOf(worst.index), 1);
    }
    const nestOf = (index: number): NestRule | undefined => {
      const parent = parentIndexOf[index];
      if (parent < 0) return undefined;
      const parentHome = homes[parent];
      const home = homes[index];
      /* Clamp to the parent's rows: a slab row outside them is not a parent
         row to offset from, and reading it as "no offset" handed the slab
         every row its parent has. Wholly outside, it hugs the nearer edge. */
      const last = parentHome.length - 1;
      let top = parentHome.findIndex((rank) => rank >= home[0]);
      let bottom =
        last - [...parentHome].reverse().findIndex((rank) => rank <= home[home.length - 1]);
      if (top < 0) top = last;
      if (bottom > last) bottom = 0;
      if (top > bottom) {
        if (home[0] > parentHome[last]) bottom = top;
        else top = bottom;
      }
      return {
        parentCdt0: own[parent].cdt0,
        topOffset: top,
        bottomOffset: last - bottom,
        parentRows: parentHome.length,
      };
    };
    const sectionDrafts: Draft[] = own.map((stratum, index) => ({
      cdt0: stratum.cdt0,
      section,
      bandAttr: stratum.bandAttr,
      bandValue: stratum.bandValue,
      home: homes[index],
      kind: isFull[index]
        ? "fullHeight"
        : isNested[index] || overlapping[index]
          ? "column"
          : "band",
      // A slab anchors on its own first bay on the base; the store has no
      // such bay, so it takes no zone-edge anchor either.
      anchors: stratum.anchors && !isFull[index] && !stratum.anchorOwnBay,
      baseWidthCm: virtualParents.has(stratum.cdt0)
        ? slabsOfParent(stratum.cdt0).reduce((sum, slab) => sum + widthOfCdt0(slab.cdt0), 0)
        : widthOfCdt0(stratum.cdt0),
      minWidthCm: virtualParents.has(stratum.cdt0)
        ? slabsOfParent(stratum.cdt0).reduce((sum, slab) => sum + minOfCdt0(slab.cdt0), 0)
        : minOfCdt0(stratum.cdt0),
      ...(isNested[index] && !isFull[index] ? { nest: nestOf(index) } : {}),
      ...(stratum.anchorOwnBay ? { anchorsOwnBay: true } : {}),
      ...(virtualParents.has(stratum.cdt0) ? { virtual: true } : {}),
    }));

    for (const carve of carvePairs) {
      const small = sectionDrafts.find((draft) => draft.cdt0 === carve.smallCdt0);
      const large = sectionDrafts.find((draft) => draft.cdt0 === carve.largeCdt0);
      if (!small || !large) continue;
      const home = uniqueSorted([...small.home, ...large.home]);
      if (!contiguousRanks(home)) {
        warnings.push(
          `"${carve.smallCdt0}" and "${carve.largeCdt0}" do not sit on neighbouring rows on the base pog, so they stay two bands.`,
        );
        continue;
      }
      const parent: Draft = {
        cdt0: carve.parentCdt0,
        section,
        bandAttr: carve.attr,
        bandValue: null,
        home,
        kind: "band",
        anchors: small.anchors || large.anchors,
        baseWidthCm: small.baseWidthCm + large.baseWidthCm,
        minWidthCm: small.minWidthCm + large.minWidthCm,
        carve: { ...carve, section },
      };
      sectionDrafts.splice(sectionDrafts.indexOf(small), 1, parent);
      sectionDrafts.splice(sectionDrafts.indexOf(large), 1);
    }
    drafts.push(...sectionDrafts);
  }

  const bandOf = (draft: Draft, order: number): BandRule => ({
    cdt0: draft.cdt0,
    section: draft.section,
    bandAttr: draft.bandAttr,
    bandValue: draft.bandValue,
    order,
    kind: draft.kind,
    baseRows: draft.home.length,
    baseHome: [...draft.home],
    baseShelves: shelfCount,
    baseRowShare: draft.home.length / shelfCount,
    baseWidthCm: draft.baseWidthCm,
    minWidthCm: draft.minWidthCm,
    anchorsAtZoneEdge: draft.anchors,
    ...(draft.carve ? { carve: draft.carve } : {}),
    ...(draft.anchorsOwnBay ? { anchorsOwnBay: true } : {}),
    ...(draft.virtual ? { virtual: true } : {}),
  });

  const bands: BandRule[] = [];
  for (const section of bySection.keys()) {
    const own = drafts
      .filter((draft) => draft.section === section)
      .sort((a, b) => (a.home[0] ?? 0) - (b.home[0] ?? 0) || a.cdt0.localeCompare(b.cdt0));
    own.forEach((draft, index) => {
      const band = bandOf(draft, index);
      if (draft.kind !== "column") {
        bands.push(band);
        return;
      }
      // A nested slab follows its parent's rows; no span to measure.
      if (draft.nest) {
        bands.push({ ...band, nest: draft.nest });
        return;
      }
      // The span: from the band owning the column's top row to the band owning
      // its bottom row, naming which edge of each.
      const rowBands = own.filter((other) => other !== draft && other.kind === "band");
      const ownerOf = (rank: number) => rowBands.find((other) => other.home.includes(rank));
      const top = draft.home[0];
      const bottom = draft.home[draft.home.length - 1];
      const from = ownerOf(top);
      const to = ownerOf(bottom);
      if (!from || !to) {
        warnings.push(
          `"${draft.cdt0}" runs across rows ${draft.home.join(",")} of "${section}" and no band owns its top or bottom row, so it keeps the section's whole height.`,
        );
        bands.push({ ...band, kind: "fullHeight" });
        return;
      }
      const span: BandSpan =
        from === to
          ? { fromCdt0: from.cdt0, fromEdge: "top", toCdt0: to.cdt0, toEdge: "bottom" }
          : {
              fromCdt0: from.cdt0,
              fromEdge: top === from.home[from.home.length - 1] ? "bottom" : "top",
              toCdt0: to.cdt0,
              toEdge: bottom === to.home[to.home.length - 1] ? "bottom" : "top",
            };
      bands.push({ ...band, span });
    });
  }
  bands.sort((a, b) => orderOf(a.cdt0) - orderOf(b.cdt0) || a.cdt0.localeCompare(b.cdt0));
  const out = bands as BandRule[] & { warnings: readonly string[] };
  Object.defineProperty(out, "warnings", { value: warnings, enumerable: false });
  return out;
};

export interface StoreDemand {
  minCm: number;

  baseCm: number;
}

export interface FixtureSideInput {
  rules: PartitionRules;
  bands: readonly BandRule[];
  fixture: StoreFixture;

  sectionSales: Readonly<Record<string, number>>;

  skuCountByStratum: ReadonlyMap<string, number>;

  demandByStratum?: ReadonlyMap<string, StoreDemand>;
  /** Soft zone lines: every boundary bay is open to both neighbouring zones
   *  (the roomier zone lends it), so the solver draws the line inside the
   *  bay. Mirrors the emitter's `fences.sharedSeams`. */
  openSeams?: boolean;
  /** cdt0 → brand columns the store ships in it: its (cdt0, cdt1) pairs AFTER
   *  pooling, a pool counting as one column. */
  pairCountByStratum?: ReadonlyMap<string, number>;

  widestPackCmByStratum?: ReadonlyMap<string, number>;
  /** The category's unit, for the words only; absent, `categoryLengthUnitOf`'s default. */
  lengthUnit?: LengthUnit;
}

export interface ZoneSeam {
  leftRun: number;
  rightRun: number;
}

const assignBays = (counts: readonly number[], bays: readonly StoreBay[]): number[][] => {
  const out: number[][] = [];
  let cursor = 0;
  for (const count of counts) {
    out.push(bays.slice(cursor, cursor + count).map((bay) => bay.bayNo));
    cursor += count;
  }
  return out;
};

const allocateRows = (
  ranks: readonly number[],
  bands: readonly { cdt0: string; weight: number; hasCarve: boolean }[],
): { rows: Map<string, number[]>; warnings: string[] } => {
  const warnings: string[] = [];
  const rows = new Map<string, number[]>();
  if (bands.length === 0 || ranks.length === 0) return { rows, warnings };
  const total = bands.reduce((sum, band) => sum + Math.max(band.weight, 0), 0);
  const quotas = bands.map((band) =>
    total > 0 ? (ranks.length * Math.max(band.weight, 0)) / total : ranks.length / bands.length,
  );
  const counts = quotas.map((quota) => Math.max(1, Math.floor(quota)));
  const held = () => counts.reduce((acc, count) => acc + count, 0);
  for (let guard = 0; held() > ranks.length && guard < ranks.length + bands.length; guard += 1) {
    const shrink = counts
      .map((count, index) => ({ index, over: count - quotas[index], count }))
      .filter((entry) => entry.count > 1)
      .sort((a, b) => b.over - a.over || a.index - b.index)[0];
    if (!shrink) break;
    counts[shrink.index] -= 1;
  }
  let left = ranks.length - held();
  const pool = counts
    .map((_, index) => index)
    .sort(
      (a, b) => quotas[b] - counts[b] - (quotas[a] - counts[a]) || quotas[b] - quotas[a] || a - b,
    );
  while (left > 0 && pool.length > 0) {
    const top = pool[0];
    const bar = quotas[top] - counts[top] - ROW_SHARE_TIE;
    const tied = pool.filter((index) => quotas[index] - counts[index] >= bar);
    const winner = tied.find((index) => bands[index].hasCarve) ?? top;
    counts[winner] += 1;
    pool.splice(pool.indexOf(winner), 1);
    left -= 1;
  }

  let cursor = 0;
  bands.forEach((band, index) => {
    const take = ranks.slice(cursor, cursor + counts[index]);
    cursor += counts[index];
    if (take.length > 0) {
      rows.set(band.cdt0, take);
      return;
    }
    rows.set(band.cdt0, [ranks[ranks.length - 1]]);
    warnings.push(
      `"${band.cdt0}" shares the bottom row — ${ranks.length} shelves for ${bands.length} bands.`,
    );
  });
  return { rows, warnings };
};

/** One zone under allocation: the sections it carries, the runs it owns and
 *  any neighbour's run left open to it as well. */
interface ZoneWork {
  sections: string[];
  share: number;
  interleaveWith: "left" | "right" | null;
  /** Bays this zone owns. */
  bays: number[];
  /** A neighbour's bay left open to this zone as well (the seam). */
  shared: number[];
}

/** Runs per zone, shared boundary runs, and where a hard zone line falls. The
 *  emitter takes only this; its rows come from the base wall's own strata. */
export interface ZoneAllocation {
  zones: readonly PartitionZone[];
  zoneSeams: readonly ZoneSeam[];
  warnings: readonly string[];
}

/** Everything the two stages of the fixture side share: the fixture's runs and
 *  ranks, each band's demand and columns, and the wall's own fill. Built once
 *  per call by each stage; it pushes only the FIXTURE's own warnings (poles,
 *  an assortment that does not fit), which the zone stage reports. */
const fixtureSideBasis = (input: FixtureSideInput) => {
  const lengthUnit = input.lengthUnit ?? categoryLengthUnitOf();
  const warnings: string[] = [];
  const bays = [...input.fixture.bays].sort((a, b) => a.bayNo - b.bayNo);
  const totalBays = bays.length;
  const totalCm = shelfCmOf(input.fixture);
  const bayOf = new Map(bays.map((bay) => [bay.bayNo, bay]));
  const runCmOf = (bayNo: number) => bayShelfCmOf(bayOf.get(bayNo)!);

  const fullRunCm = Math.max(...bays.map(bayShelfCmOf), 0);

  const physicalOf = (bayNo: number) => bayOf.get(bayNo)?.physicalBayNo ?? bayNo;
  const physicalBaysIn = (bayNos: readonly number[]) => [...new Set(bayNos.map(physicalOf))];
  const runsPerPhysical = new Map<number, number>();
  for (const bay of bays) {
    const physical = physicalOf(bay.bayNo);
    runsPerPhysical.set(physical, (runsPerPhysical.get(physical) ?? 0) + 1);
  }
  const isFragment = (bayNo: number) => (runsPerPhysical.get(physicalOf(bayNo)) ?? 1) > 1;

  const runsSplitABay = physicalBaysIn(bays.map((bay) => bay.bayNo)).length !== bays.length;
  for (const physical of physicalBaysIn(bays.map((bay) => bay.bayNo))) {
    const own = bays.filter((bay) => physicalOf(bay.bayNo) === physical);
    const dead = own.reduce((sum, bay) => sum + (bay.deadRightCm ?? 0) + (bay.deadLeftCm ?? 0), 0);
    if (own.length > 1) {
      warnings.push(
        `Bay ${physical} is split by a ${lengthLabel(dead, lengthUnit)} pole, so it holds ${own.length} shorter runs (${own
          .map((bay) => lengthLabel(bay.widthCm, lengthUnit))
          .join(" and ")}). Nothing crosses the pole.`,
      );
      continue;
    }
    if (dead > 0) {
      warnings.push(
        `Bay ${physical} loses ${lengthLabel(dead, lengthUnit)} to a pole, so it holds ${lengthLabel(own[0].widthCm, lengthUnit)} of shelf.`,
      );
    }
  }
  const ranksOf = (bayNo: number) =>
    uniqueSorted((bayOf.get(bayNo)?.shelves ?? []).map((shelf) => shelf.rank));

  const countOf = (cdt0: string) => Math.max(input.skuCountByStratum.get(cdt0) ?? 0, 0);
  /** The slabs nested in a band (stacked brands carved out of it). */
  const slabsOf = (band: BandRule): BandRule[] =>
    input.bands.filter((other) => other.nest?.parentCdt0 === band.cdt0);
  const shipsOf = (band: BandRule): number => {
    if (band.virtual) return slabsOf(band).reduce((sum, slab) => sum + countOf(slab.cdt0), 0);
    if (!band.carve) return countOf(band.cdt0);
    const own = input.skuCountByStratum.get(band.cdt0);
    return own !== undefined
      ? Math.max(own, 0)
      : countOf(band.carve.smallCdt0) + countOf(band.carve.largeCdt0);
  };
  const demandOf = (band: BandRule): StoreDemand => {
    const own = input.demandByStratum?.get(band.cdt0);
    if (own) return own;
    if (band.virtual) {
      return slabsOf(band).reduce(
        (sum, slab) => {
          const demand = demandOf(slab);
          return { minCm: sum.minCm + demand.minCm, baseCm: sum.baseCm + demand.baseCm };
        },
        { minCm: 0, baseCm: 0 },
      );
    }
    if (band.carve) {
      const small = input.demandByStratum?.get(band.carve.smallCdt0);
      const large = input.demandByStratum?.get(band.carve.largeCdt0);
      if (small || large) {
        return {
          minCm: (small?.minCm ?? 0) + (large?.minCm ?? 0),
          baseCm: (small?.baseCm ?? 0) + (large?.baseCm ?? 0),
        };
      }
    }
    return { minCm: band.minWidthCm, baseCm: band.baseWidthCm };
  };
  const shipping = input.bands.filter((band) => shipsOf(band) > 0);
  /** A band's demand for ROW purposes: its own items plus every slab nested
   *  in it, since the slabs stand in its rows. A virtual parent's own demand
   *  already is its slabs' sum. */
  const demandWithSlabs = (band: BandRule): StoreDemand => {
    const own = demandOf(band);
    if (band.virtual) return own;
    return slabsOf(band).reduce(
      (sum, slab) => {
        const demand = demandOf(slab);
        return { minCm: sum.minCm + demand.minCm, baseCm: sum.baseCm + demand.baseCm };
      },
      { minCm: own.minCm, baseCm: own.baseCm },
    );
  };
  /** Wall and zone totals count every real band once: a virtual parent's
   *  demand is its slabs', which are bands of their own. */
  const counted = shipping.filter((band) => !band.virtual);
  const wallMin = counted.reduce((sum, band) => sum + demandOf(band).minCm, 0);
  const wallBase = counted.reduce((sum, band) => sum + demandOf(band).baseCm, 0);
  /** Roomy: the store's shelf-cm holds the assortment at base facings; rows
   *  then divide on base-facings demand, otherwise on min-facings demand. */
  const roomy = wallBase <= totalCm;
  const wallFill = totalCm > 0 ? wallBase / totalCm : 0;
  if (wallMin > totalCm) {
    warnings.push(
      `The assortment needs ${lengthLabel(wallMin, lengthUnit)} at one facing each and this store holds ${lengthLabel(totalCm, lengthUnit)}.`,
    );
  }

  /** Brand columns a band must stand, a pool counting as one. Unknown (no
   *  `pairCountByStratum`) reads 0. */
  const columnsOfBand = (band: BandRule): number => {
    const own = input.pairCountByStratum?.get(band.cdt0);
    if (own !== undefined) return Math.max(own, 0);
    if (!band.carve) return 0;
    return (
      Math.max(input.pairCountByStratum?.get(band.carve.smallCdt0) ?? 0, 0) +
      Math.max(input.pairCountByStratum?.get(band.carve.largeCdt0) ?? 0, 0)
    );
  };

  const widestPackOf = (band: BandRule): number => {
    const own = input.widestPackCmByStratum?.get(band.cdt0);
    if (own !== undefined) return Math.max(own, 0);
    if (!band.carve) return 0;
    return Math.max(
      input.widestPackCmByStratum?.get(band.carve.smallCdt0) ?? 0,
      input.widestPackCmByStratum?.get(band.carve.largeCdt0) ?? 0,
    );
  };
  /** The ranks a section's cake divides among its row bands in a bay. A partial
   *  cake keeps its base gap, scaled and on the same edge. */
  const cakeRanksOf = (bandsOfSection: readonly BandRule[], ranks: readonly number[]): number[] => {
    const rowBands = bandsOfSection.filter((band) => band.kind === "band");
    if (rowBands.length === 0 || !bandsOfSection.some((band) => band.kind === "fullHeight")) {
      return [...ranks];
    }
    const covered = uniqueSorted(rowBands.flatMap((band) => [...(band.baseHome ?? [])]));
    const baseShelves = Math.max(...rowBands.map((band) => band.baseShelves), 0);
    if (covered.length === 0 || covered.length >= baseShelves) return [...ranks];
    const gap = Array.from({ length: baseShelves }, (_, index) => index + 1).filter(
      (rank) => !covered.includes(rank),
    );
    const topGap = gap.every((rank) => rank < covered[0]);
    const bottomGap = gap.every((rank) => rank > covered[covered.length - 1]);
    if (!topGap && !bottomGap) return [...ranks];
    const gapHere = Math.max(
      0,
      Math.min(
        Math.round((gap.length / baseShelves) * ranks.length),
        ranks.length - rowBands.length,
      ),
    );
    return topGap ? ranks.slice(gapHere) : ranks.slice(0, ranks.length - gapHere);
  };
  const rowWeightOf = (band: BandRule): number => {
    const demand = demandWithSlabs(band);
    const weight = roomy ? demand.baseCm : demand.minCm;
    return weight > 0 ? weight : band.baseRowShare;
  };

  const rowsOfSection = (bandsOfSection: readonly BandRule[], runs: readonly number[]) => {
    const rowBands = bandsOfSection.filter((band) => band.kind === "band");
    const perRun = new Map<number, Map<string, number[]>>();
    for (const bayNo of runs) {
      perRun.set(
        bayNo,
        allocateRows(
          cakeRanksOf(bandsOfSection, ranksOf(bayNo)),
          rowBands.map((band) => ({
            cdt0: band.cdt0,
            weight: rowWeightOf(band),
            hasCarve: band.carve !== undefined,
          })),
        ).rows,
      );
    }
    return perRun;
  };

  return {
    warnings,
    bays,
    totalBays,
    totalCm,
    bayOf,
    runCmOf,
    fullRunCm,
    physicalBaysIn,
    isFragment,
    runsSplitABay,
    ranksOf,
    countOf,
    slabsOf,
    shipsOf,
    demandOf,
    shipping,
    demandWithSlabs,
    roomy,
    wallFill,
    columnsOfBand,
    widestPackOf,
    cakeRanksOf,
    rowsOfSection,
  };
};

/** The zone stage alone: runs to zones by section sales, donations, the
 *  interleave merge and the seams. The emitter runs it against the base's own
 *  fixture so its partition bans follow the same zoning. */
export const allocateZoneBays = (input: FixtureSideInput): ZoneAllocation => {
  const {
    warnings,
    bays,
    totalBays,
    totalCm,
    bayOf,
    runCmOf,
    fullRunCm,
    physicalBaysIn,
    runsSplitABay,
    ranksOf,
    demandOf,
    shipping,
    wallFill,
    columnsOfBand,
    widestPackOf,
    rowsOfSection,
  } = fixtureSideBasis(input);
  const lengthUnit = input.lengthUnit ?? categoryLengthUnitOf();

  let groups: ZoneWork[] = input.rules.zones.map((zone) => ({
    sections: [...zone.sections],
    share: zone.share,
    interleaveWith: zone.interleaveWith ?? null,
    bays: [],
    shared: [],
  }));
  const bandsOfZone = (zone: ZoneWork) =>
    shipping.filter((band) => zone.sections.includes(band.section));
  for (const zone of groups.filter((entry) => bandsOfZone(entry).length === 0)) {
    warnings.push(
      `This store ranges nothing from "${zone.sections.join(" + ")}", so it gets no bays.`,
    );
  }
  groups = groups.filter((zone) => bandsOfZone(zone).length > 0);
  if (groups.length === 0) {
    return { zones: [], zoneSeams: [], warnings };
  }
  if (groups.length > totalBays) {
    warnings.push(
      `This store has ${totalBays} run(s) for ${groups.length} zones, so the last zones get none.`,
    );
  }

  const capacityOf = (zone: ZoneWork) =>
    zone.bays.reduce((sum, bayNo) => sum + bayShelfCmOf(bayOf.get(bayNo)!), 0);
  const minOf = (zone: ZoneWork) =>
    bandsOfZone(zone)
      .filter((band) => !band.virtual)
      .reduce((sum, band) => sum + demandOf(band).minCm, 0);
  const baseOf = (zone: ZoneWork) =>
    bandsOfZone(zone)
      .filter((band) => !band.virtual)
      .reduce((sum, band) => sum + demandOf(band).baseCm, 0);
  const nameOfZone = (zone: ZoneWork) => zone.sections.join(" + ");

  /** Lowest ratio of a zone's mean column width to two facings of its widest
   *  pack; under 1 the columns are slivers and the zone is crowded. */
  const crowdingOf = (zone: ZoneWork, runs: readonly number[]): number => {
    if (!input.pairCountByStratum || !input.widestPackCmByStratum || runs.length === 0) {
      return Number.POSITIVE_INFINITY;
    }
    let worst = Number.POSITIVE_INFINITY;
    const own = bandsOfZone(zone);
    for (const section of [...new Set(own.map((band) => band.section))]) {
      const bandsOfSection = own.filter((band) => band.section === section);
      const perRun = rowsOfSection(bandsOfSection, runs);
      for (const band of bandsOfSection) {
        const columns = columnsOfBand(band);
        const floor = 2 * widestPackOf(band);
        if (columns <= 0 || floor <= 0) continue;
        let shelfCm = 0;
        let deepest = 0;
        for (const bayNo of runs) {
          const rows =
            band.kind === "band"
              ? (perRun.get(bayNo)?.get(band.cdt0)?.length ?? 0)
              : ranksOf(bayNo).length;
          shelfCm += (bayOf.get(bayNo)?.widthCm ?? 0) * rows;
          deepest = Math.max(deepest, rows);
        }
        if (deepest <= 0) continue;
        worst = Math.min(worst, shelfCm / deepest / columns / floor);
      }
    }
    return worst;
  };

  const allocate = (): number[] => {
    const salesOfZone = (zone: ZoneWork) =>
      zone.sections.reduce(
        (sum, section) => sum + Math.max(input.sectionSales[section] ?? 0, 0),
        0,
      );
    const salesTotal = groups.reduce((sum, zone) => sum + salesOfZone(zone), 0);
    const weights =
      salesTotal > 0 ? groups.map(salesOfZone) : groups.map((zone) => Math.max(zone.share, 0));
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
    const targets = weights.map((weight) =>
      weightTotal > 0 ? (weight / weightTotal) * totalCm : totalCm / groups.length,
    );
    const counts: number[] = [];
    let cursor = 0;
    groups.forEach((_, index) => {
      const spare = groups.length - index - 1;
      let runs = 0;
      let held = 0;
      while (cursor + runs < bays.length - spare) {
        const next = runCmOf(bays[cursor + runs].bayNo);
        const owed = runs === 0 || held < fullRunCm - 1e-6;
        if (!owed && held + next > targets[index] + 1e-6) break;
        held += next;
        runs += 1;
      }
      counts.push(Math.max(runs, 1));
      cursor += counts[index];
    });
    const held = () => counts.reduce((acc, count) => acc + count, 0);
    for (let guard = 0; held() > bays.length && guard < bays.length + groups.length; guard += 1) {
      const shrink = counts
        .map((count, index) => ({ index, count }))
        .filter((entry) => entry.count > 1)
        .sort((a, b) => b.count - a.count || a.index - b.index)[0];
      if (!shrink) break;
      counts[shrink.index] -= 1;
    }
    // Largest remainder first, then crowded zones worst-first, then shelf-cm deficit.
    for (let left = bays.length - held(); left > 0; left -= 1) {
      const owned = assignBays(counts, bays);
      const winner = counts
        .map((_, index) => {
          const deficit =
            targets[index] - owned[index].reduce((sum, bayNo) => sum + runCmOf(bayNo), 0);
          return {
            index,
            crowding: crowdingOf(groups[index], owned[index]),
            deficit,
            owed: deficit > fullRunCm / 2 ? 1 : 0,
          };
        })
        .sort(
          (a, b) =>
            b.owed - a.owed ||
            Math.min(a.crowding, 1) - Math.min(b.crowding, 1) ||
            b.deficit - a.deficit ||
            a.index - b.index,
        )[0];
      counts[winner.index] += 1;
    }
    assignBays(counts, bays).forEach((owned, index) => {
      groups[index].bays = owned;
    });
    return counts;
  };

  const mergeWithNeighbour = (index: number, why: string): boolean => {
    const side = groups[index].interleaveWith;
    const other = side === "left" ? index - 1 : side === "right" ? index + 1 : -1;
    if (other < 0 || other >= groups.length) return false;
    const lo = Math.min(index, other);
    const partner = groups[index === lo ? lo + 1 : lo];
    const merged: ZoneWork = {
      sections: [...groups[lo].sections, ...groups[lo + 1].sections],
      share: groups[lo].share + groups[lo + 1].share,
      interleaveWith: null,
      bays: [],
      shared: [],
    };
    warnings.push(`${why} It shares bays with "${nameOfZone(partner)}", the section beside it.`);
    groups.splice(lo, 2, merged);
    return true;
  };

  /** Zones squeezed harder than the wall at their base facings that had no
   *  neighbour to merge into (a clean bay line on the base): the seam pass
   *  below opens the boundary bay to them instead. */
  const standsAlone = new Set<ZoneWork>();
  for (let round = 0; round < input.rules.zones.length + 2; round += 1) {
    const counts = allocate();

    for (let guard = 0; guard < totalBays * 2; guard += 1) {
      const needy = groups.findIndex((zone) => capacityOf(zone) < minOf(zone) - 1e-6);
      if (needy < 0) break;
      const donor = groups
        .map((zone, index) => ({ index, slack: capacityOf(zone) - minOf(zone) }))
        .filter((entry) => entry.index !== needy && counts[entry.index] > 1)
        .filter((entry) => {
          const trial = [...counts];
          trial[entry.index] -= 1;
          trial[needy] += 1;
          const owned = assignBays(trial, bays);
          const donorCm = owned[entry.index].reduce(
            (sum, bayNo) => sum + bayShelfCmOf(bayOf.get(bayNo)!),
            0,
          );
          if (donorCm < minOf(groups[entry.index]) - 1e-6) return false;

          return crowdingOf(groups[entry.index], owned[entry.index]) >= 1;
        })
        .sort(
          (a, b) => b.slack - a.slack || Math.abs(a.index - needy) - Math.abs(b.index - needy),
        )[0];

      const fillOf = (trial: readonly number[]) => {
        const owned = assignBays(trial, bays);
        return Math.max(
          ...groups.map((zone, index) => {
            const capacity = owned[index].reduce(
              (sum, bayNo) => sum + bayShelfCmOf(bayOf.get(bayNo)!),
              0,
            );
            return capacity > 0 ? minOf(zone) / capacity : Number.POSITIVE_INFINITY;
          }),
        );
      };
      const shared =
        donor === undefined
          ? groups
              .map((_, index) => index)
              .filter((index) => index !== needy && counts[index] > 1)
              .map((index) => {
                const trial = [...counts];
                trial[index] -= 1;
                trial[needy] += 1;
                return { index, worst: fillOf(trial), trial };
              })

              .filter(
                (entry) =>
                  crowdingOf(groups[entry.index], assignBays(entry.trial, bays)[entry.index]) >= 1,
              )
              .filter((entry) => entry.worst < fillOf(counts) - 1e-9)
              .sort((a, b) => a.worst - b.worst || a.index - b.index)[0]
          : undefined;
      const source = donor ?? shared;
      if (!source) break;
      counts[source.index] -= 1;
      counts[needy] += 1;
      assignBays(counts, bays).forEach((owned, index) => {
        groups[index].bays = owned;
      });
      warnings.push(
        `"${nameOfZone(groups[needy])}" needs ${lengthLabel(minOf(groups[needy]), lengthUnit)} at one facing each, so it takes a bay from "${nameOfZone(groups[source.index])}".`,
      );
    }

    const over = groups.findIndex((zone) => {
      const capacity = capacityOf(zone);
      return capacity > 0 && baseOf(zone) / capacity > wallFill + ZONE_OVERFLOW_TOLERANCE;
    });
    if (over >= 0) {
      const why = `"${nameOfZone(groups[over])}" wants ${lengthLabel(baseOf(groups[over]), lengthUnit)} and the ${groups[over].bays.length} bay(s) its share earns hold ${lengthLabel(capacityOf(groups[over]), lengthUnit)}, fuller than the rest of the wall.`;
      if (mergeWithNeighbour(over, why)) continue;
      standsAlone.add(groups[over]);
      warnings.push(`${why} It stands alone: no section beside it shares a bay on the base pog.`);
    }

    const short = groups.findIndex((zone) => capacityOf(zone) < minOf(zone) - 1e-6);

    const canBorrow =
      short >= 0 &&
      input.pairCountByStratum !== undefined &&
      input.widestPackCmByStratum !== undefined &&
      [short - 1, short + 1].some(
        (other) => other >= 0 && other < groups.length && groups[other].bays.length > 0,
      );
    if (short >= 0 && !canBorrow) {
      const why = `"${nameOfZone(groups[short])}" needs ${lengthLabel(minOf(groups[short]), lengthUnit)} at one facing each and its ${groups[short].bays.length} bay(s) hold ${lengthLabel(capacityOf(groups[short]), lengthUnit)}.`;
      const pair = groups
        .map((zone, index) => ({ index, cost: minOf(zone) }))
        .filter((entry) => {
          const side = groups[entry.index].interleaveWith;
          const other = side === "left" ? entry.index - 1 : side === "right" ? entry.index + 1 : -1;
          return other >= 0 && other < groups.length;
        })
        .sort((a, b) => a.cost - b.cost || a.index - b.index)[0];
      if (pair && mergeWithNeighbour(pair.index, why)) continue;
      warnings.push(`${why} The assortment does not fit this store.`);
    }
    break;
  }

  const fitOf = (zone: ZoneWork) => {
    const capacity = capacityOf(zone);
    return capacity > 0 ? minOf(zone) / capacity : Number.POSITIVE_INFINITY;
  };
  const crowdedIn = (index: number) => crowdingOf(groups[index], groups[index].bays) < 1;
  /** How far a zone's base facings overrun its bays; 0 unless they do not fit at
   *  all and no neighbour was found to merge into. A seam is not a merge. */
  const overflowOf = (zone: ZoneWork) => {
    if (!standsAlone.has(zone)) return 0;
    const capacity = capacityOf(zone);
    if (!(capacity > 0)) return 0;
    return Math.max(0, baseOf(zone) / capacity - (1 + ZONE_OVERFLOW_TOLERANCE));
  };
  const seamTaken = new Set<number>();
  // A zone that cannot hold its assortment at one facing, that cannot stand
  // its brand columns without slivering them, or whose base facings overflow
  // its bays with no neighbour to merge into, is lent its neighbour's
  // boundary bay. Worst off first.
  const wanting = groups
    .map((zone, index) => ({
      index,
      fit: fitOf(zone),
      crowding: crowdingOf(zone, zone.bays),
      overflow: overflowOf(zone),
    }))
    .map((entry) => ({
      ...entry,
      severity: Math.max(
        entry.fit - (1 + ZONE_OVERFLOW_TOLERANCE),
        entry.crowding < 1 ? 1 - entry.crowding : 0,
        entry.overflow,
      ),
    }))
    .filter((entry) => entry.severity > 0)
    .sort((a, b) => b.severity - a.severity || a.index - b.index);
  for (const { index, fit, crowding, overflow } of wanting) {
    const zone = groups[index];
    const lender = [index - 1, index + 1]
      .filter(
        (other) =>
          other >= 0 &&
          other < groups.length &&
          groups[other].bays.length > 0 &&
          !seamTaken.has(Math.min(index, other)),
      )
      // A zone that cannot stand its own columns has none to lend.
      .filter((other) => fit > 1 + ZONE_OVERFLOW_TOLERANCE || overflow > 0 || !crowdedIn(other))
      .sort((a, b) => fitOf(groups[a]) - fitOf(groups[b]) || a - b)[0];
    if (lender === undefined) continue;
    const neighbour = groups[lender];
    const seam = lender < index ? neighbour.bays[neighbour.bays.length - 1] : neighbour.bays[0];
    zone.shared.push(seam);
    seamTaken.add(Math.min(index, lender));
    warnings.push(
      fit > 1 + ZONE_OVERFLOW_TOLERANCE
        ? `"${nameOfZone(zone)}" needs ${(100 * fit).toFixed(0)}% of the ${zone.bays.length} bay(s) it owns at one facing each, so bay ${seam} is open to it as well as to "${nameOfZone(neighbour)}".`
        : overflow > 0
          ? `"${nameOfZone(zone)}" wants ${lengthLabel(baseOf(zone), lengthUnit)} at its base facings and the ${zone.bays.length} bay(s) it owns hold ${lengthLabel(capacityOf(zone), lengthUnit)}; the base drew a clean line beside it, so bay ${seam} is open to it as well as to "${nameOfZone(neighbour)}" rather than merging the two.`
          : `"${nameOfZone(zone)}" cannot give its brands a column wide enough to shop in the ${zone.bays.length} bay(s) it owns, so bay ${seam} is open to it as well as to "${nameOfZone(neighbour)}". (${(100 * crowding).toFixed(0)}% of the width a column needs.)`,
    );
  }

  // Soft zone lines, when asked: a boundary bay still lacking a seam opens to
  // the fuller of the two zones (the roomier one lends it), so the solver
  // draws the line inside that bay instead of on it.
  if (input.openSeams) {
    for (let index = 0; index + 1 < groups.length; index += 1) {
      if (seamTaken.has(index)) continue;
      const left = groups[index];
      const right = groups[index + 1];
      if (left.bays.length === 0 || right.bays.length === 0) continue;
      const fullOf = (zone: ZoneWork) => {
        const capacity = capacityOf(zone);
        return capacity > 0 ? baseOf(zone) / capacity : Number.POSITIVE_INFINITY;
      };
      const borrower = fullOf(left) >= fullOf(right) ? left : right;
      const lender = borrower === left ? right : left;
      const seam = lender === right ? right.bays[0] : left.bays[left.bays.length - 1];
      borrower.shared.push(seam);
      seamTaken.add(index);
      warnings.push(
        `Soft line: bay ${seam} is open to "${nameOfZone(borrower)}" as well as to "${nameOfZone(lender)}", so the solver draws the line inside it.`,
      );
    }
  }

  const zones: PartitionZone[] = groups.map((zone) => ({
    sections: zone.sections,
    share: Number(zone.share.toFixed(4)),
    bays: zone.bays,
    ...(runsSplitABay ? { physicalBays: physicalBaysIn(zone.bays) } : {}),
    ...(zone.shared.length > 0
      ? { sharedBays: [...new Set(zone.shared)].sort((a, b) => a - b) }
      : {}),
  }));
  const zoneSeams: ZoneSeam[] = [];
  for (let index = 0; index + 1 < groups.length; index += 1) {
    const left = groups[index];
    const right = groups[index + 1];
    const leftLast = left.bays[left.bays.length - 1];
    const rightFirst = right.bays[0];
    if (leftLast === undefined || rightFirst === undefined) continue;

    if (left.shared.includes(rightFirst) || right.shared.includes(leftLast)) continue;
    zoneSeams.push({ leftRun: leftLast, rightRun: rightFirst });
  }

  return { zones, zoneSeams, warnings };
};
